import { Card, CardRank, CardSuit, TrumpMode } from '../card/Card';
import { AppLocalization } from '../localization/AppLocalization';
import { GamePlayDirection } from '../managers/AppSettings';
import { CardRankProps, CardsManager } from '../managers/CardsManager';
import { Player } from '../player/Player';
import { TableScreen } from '../screen/TableScreen';
import { TableStateMachine, TrumpSuit } from '../state/TableStateMachine';
import { CardSuitToTrumpSuit } from '../utils/CardSuitToTrumpSuit';
import { TrumpSuitToCardSuit } from '../utils/TrumpsSuitToCardSuit';
import { DeclarationType, PlayerDeclaration } from './DeclarationsManager';
import { GetPlayerSide } from '../utils/GetPlayerSide';
import { OptionsDataManager } from '../managers/OptionsDataManager';
import { PopupBinaryChoiceType } from '../popup/PopupBinary';
import { Players } from '../player/Players';
import { OptionsItemKeys } from '../managers/LayoutDataManager';
import {
	CardData,
	ContraBidChoice,
	HintsSharedLocData,
	OptionsItemLiveData,
	PlayerCoincheBidChoice,
	TableData,
	TrickData,
} from '../../types/types';
import { CardSuitToPopupBiddingChoiceType } from '../utils/CardSuitToPopupBiddingChoiceType';
import { PopupBiddingChoiceType } from '../popup/PopupBiddingTakeSuit';
import { PopupBiddingChoiceTypeToCardSuit } from '../utils/PopupBiddingChoiceTypeToCardSuit';
import { TrumpSuitToPopupBiddingChoiceType } from '../utils/TrumpSuitToPopupBiddingChoiceType';
import { PopupBiddingChoiceTypeToTrumpSuit } from '../utils/PopupBiddingChoiceTypeToTrumpSuit';
import { CapitalizeFirstLetter } from '../../utils/CapitalizeFirstLetter';
import { GetCardSuitIconTextureStatesData } from '../utils/GetCardSuitIconTextureData';
import { GetCardSuitsExcept } from '../utils/GetCardSuitsExcept';

export enum PlayerSide {
	US,
	THEM,
	/**
	 * Is etermining 'winning side usinng PlayerSize, NONE means BOTH eg a 'TIE' (both sides same score?)
	 */
	NONE,
}

/**
 * Value assigned to each card when calculating for Bid (Belote: TAKE/CHOOSE, Coinche|Contree: ?)
 * Unbeatables: from highest rank to lowest, gapless, and first card must be highest in TrumpSuit
 *              (remember each TRUMP/NT/AT have their own rank indexes)
 * NOTRUMPS and ALLTRUMPS: only count unbeatables
 */
export enum BidCardType {
	/**
	 * T/O only
	 */
	TRUMP_UNBEATABLE,
	TRUMP_MINOR,
	OTHER_UNBEATABLE,
	/**
	 * NT/AT only
	 */
	NT_AT_UNBEATABLE,
}

export enum AILevel {
	BEGINNER = 0,
	INTERMEDIATE = 1,
	EXPERT = 2,
}

export type TrickWinnerData = {
	playerIndex: number;
	trickCardIndex: number;
};

export class TableAI {
	public table: TableScreen;
	/**
	 * Last resolved playableCards
	 */
	protected playableCards: Card[];
	/**
	 * Subset of playableCards
	 */
	protected suggestedCards: Card[];
	/**
	 * Suggested card for bot to play
	 * If no card is suggested (null), play lowest card or random card (deps on AI level)
	 */
	protected botPlayCard: Card;
	/**
	 * Last resolved 'say' text
	 */
	protected hintText: string;
	/**
	 * Last suggested Bidding choice type for AI bot
	 * applies to:
	 * - Belote: PopupChooseSuit, PopupTakeSuit
	 * - Coinche / Contree? want to split the task as well (probably. need hints to show as well)
	 */
	// protected lastBidChoiceType: PopupBiddingChoiceType;
	protected bidCardTypeToBidCardValue: Map<BidCardType, number>;
	protected minValueForBid: number;
	/**
	 * Last bid choice for Belote (TakeSuit and ChooseSuit) and Coinche/Contre (MakeBid)
	 * NOTE: Coinche/Contree use the whole object, but Belote only utilizes ContraBidChoice::choiceType
	 */
	protected lastBidChoice: ContraBidChoice;
	protected aiLevel: AILevel;
	protected trickWinnerData: TrickWinnerData;

	public Init(table: TableScreen): void {
		this.table = table;

		this.lastBidChoice = {
			choiceType: PopupBiddingChoiceType.PASS,
			choiceValueIndex: -1,
		};
		this.bidCardTypeToBidCardValue = new Map<BidCardType, number>();
		this.bidCardTypeToBidCardValue.set(BidCardType.TRUMP_UNBEATABLE, 1);
		this.bidCardTypeToBidCardValue.set(BidCardType.TRUMP_MINOR, 0.5);
		this.bidCardTypeToBidCardValue.set(BidCardType.OTHER_UNBEATABLE, 0.75);
		this.bidCardTypeToBidCardValue.set(BidCardType.NT_AT_UNBEATABLE, 1);
		this.minValueForBid = 2.25;
		this.trickWinnerData = { playerIndex: -1, trickCardIndex: -1 };
	}

	public GetPlayableCards(): Card[] {
		return this.playableCards;
	}

	public GetSuggestedCards(): Card[] {
		return this.suggestedCards;
	}

	public GetHintText(): string {
		return this.hintText;
	}

	public SetAILevel(level: AILevel): void {
		this.aiLevel = level;
	}

	public GetAILevel(): AILevel {
		return this.aiLevel;
	}

	/**
	 * Resolves Bid choice for 'TakeSuitChoicePopup'
	 * Stores result in this.lastBidChoice.choiceType
	 *
	 * Nomenclature:
	 * - T = unbeatable trump (can have multiple in downward sequence from J to 7)
	 * - t = minor trump (anything, if no unbeatable Trump, or anything after dequence gap)
	 * - O = unbeatable other ([simplified] counts Aces only)
	 *
	 * Values:
	 * T = 1
	 * t = 0.5
	 * O = 0.75
	 * Aim: 3+
	 *
	 * What this does:
	 * - Sets Hints if live Player
	 * - Sets TAKE/PASS if AI Bot
	 *
	 * NOTE: You only can Bet T/O when taking Suit.
	 *       To Bet NT/AT you need to PASS, then Bet.
	 *       Thus, you should solve for NT/AT Bid here as well.
	 *
	 * @param disabledChoices Either [] or PASS, does not contain actual suit(s), like in @see ResolveChooseSuitChoice
	 *        This laso means, that AT/NT have to be resolved 'INSIDE' this function, whereas in case of @see ResolveChooseSuitChoice
	 *        they can be 'passed in' through @see disabledChoices
	 */
	public ResolveTakeSuitChoice(disabledChoices: PopupBiddingChoiceType[], tableCard: Card): void {
		const tableData: TableData = this.table.GetStateMachine().GetData();
		const playerIndex: number = tableData.lastPlayedPlayerIndex;
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		const playerCards: Card[] = player.GetCards();
		// includes faceUp card
		const allCards: Card[] = playerCards.concat(tableCard);
		/**
		 * Total suit value accessible by CardSuit key
		 */
		let bidSuitToValue: Map<TrumpSuit, number> = new Map();
		let highestTrumpCardSuit: number = -1;
		const locHints: HintsSharedLocData = AppLocalization.GetSharedData().table.hints;
		const enableNTAT: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
			OptionsItemKeys.enableNTAT,
			this.table.variationId
		);
		const isNTAT: boolean = enableNTAT && (enableNTAT.value as boolean);
		const tableCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(tableCard.suit);

		// PASS not allowed
		// DEBUG
		// disabledChoices.push(PopupBiddingChoiceType.PASS);
		if (disabledChoices.indexOf(PopupBiddingChoiceType.PASS) != -1) {
			// have to accept
			this.lastBidChoice.choiceType = PopupBiddingChoiceType.ACCEPT;
			// "h101": "You must bid ${CARD_SUIT}.",
			this.hintText = AppLocalization.GetHintTextById('h101').replace('${CARD_SUIT}', locHints.suits[tableCardSuitAsTrumpSuit]);
		}
		// PASS is allowed
		else {
			/**
			 * contains suit cards, sorted each as if each was it's own Trump, downwards
			 */
			let cardsBySuitAT: Card[][] = [];
			// same as @see cardsBySuitNT, but used in NT calculation only (OTHER calc can use this too, but we only check for Ace)
			let cardsBySuitNT: Card[][] = [];
			// collect cards by suit
			for (let i: number = 0; i < allCards.length; i++) {
				const card: Card = allCards[i];
				if (cardsBySuitAT[card.suit] == undefined) {
					cardsBySuitAT[card.suit] = [];
					cardsBySuitNT[card.suit] = [];
					bidSuitToValue.set(CardSuitToTrumpSuit(card.suit), 0);
				}
				cardsBySuitAT[card.suit].push(card);
				cardsBySuitNT[card.suit].push(card);
			}
			// sort: cards in each suit by rank from highest to lowest
			//       (EACH SUIT ARRAY BY ITS OWN SUIT AS TRUMP SUIT)
			for (let i = 0; i < cardsBySuitAT.length; i++) {
				if (cardsBySuitAT[i] != undefined) {
					let suitCardsAT: Card[] = cardsBySuitAT[i];
					let suitCardsNT: Card[] = cardsBySuitNT[i];
					const cardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(i);
					// console.log(TrumpSuit[cardSuitAsTrumpSuit]);
					// NOTE: order is descending, so 0 rank is Jack (same for AT, AT258, TRUMP)
					suitCardsAT.sort(
						(cardA: Card, cardB: Card) =>
							cardB.GetRank(TrumpMode.ALL_TRUMPS, cardSuitAsTrumpSuit) -
							cardA.GetRank(TrumpMode.ALL_TRUMPS, cardSuitAsTrumpSuit)
					);
					// NOTE: order is descending, so 0 rank is Ace (same for NT, NT130, OTHER)
					suitCardsNT.sort(
						(cardA: Card, cardB: Card) =>
							cardB.GetRank(TrumpMode.NO_TRUMPS, cardSuitAsTrumpSuit) - 
							cardA.GetRank(TrumpMode.NO_TRUMPS, cardSuitAsTrumpSuit)
					);
				}
			}
			// console.log(cardsBySuitAT.forEach(suitCards=>suitCards.map(card=>card.log())));

			// NT/AT: count hand card values if NT or AT was declared
			if (isNTAT) {
				bidSuitToValue.set(TrumpSuit.NOTRUMPS, 0);
				bidSuitToValue.set(TrumpSuit.ALLTRUMPS, 0);
				// 0=NT; 1=AT
				for (let i = 0; i < 2; i++) {
					let curTrumpSuit: TrumpSuit;
					let curBidCardType: BidCardType;
					let curTrumpMode: TrumpMode;
					if (i == 0) {
						curTrumpSuit = TrumpSuit.NOTRUMPS;
						curBidCardType = BidCardType.NT_AT_UNBEATABLE;
						curTrumpMode = TrumpMode.NO_TRUMPS;
					} else {
						curTrumpSuit = TrumpSuit.ALLTRUMPS;
						curBidCardType = BidCardType.NT_AT_UNBEATABLE;
						curTrumpMode = TrumpMode.ALL_TRUMPS;
					}
					// suits: 0..3
					// count hand card values for each suit
					for (let j = 0; j < 4; j++) {
						// if suit exists on hand
						if (cardsBySuitAT[j] != undefined) {
							let suitCards: Card[];
							if (i == 0) {
								suitCards = cardsBySuitNT[j];
							} else {
								suitCards = cardsBySuitAT[j];
							}
							// console.log(i==0?"NT":"AT");

							// starts with leading card
							let curCard: Card = suitCards[0];
							let curCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(curCard.suit);
							let lastCardRank: CardRank = curCard.GetRank(curTrumpMode, curCardSuitAsTrumpSuit);
							if (lastCardRank == 7) { // leading rank highest rank
								// console.log("UNBEATABLE");
								// console.log(`+${this.bidCardTypeToBidCardValue.get(curBidCardType)}`);
								bidSuitToValue.set(
									curTrumpSuit,
									bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curBidCardType)
								);
								// if more cards in this suit
								if (suitCards.length > 1) {
									// count downwards all those cards with rank 1 lower than the previous card
									for (let k = 1; k < suitCards.length; k++) {
										curCard = suitCards[k];
										curCardSuitAsTrumpSuit = CardSuitToTrumpSuit(curCard.suit);
										const curCardRank: CardRank = curCard.GetRank(curTrumpMode, curCardSuitAsTrumpSuit);
										if (curCardRank == lastCardRank - 1) {
											// console.log(`+${this.bidCardTypeToBidCardValue.get(curBidCardType)}`);
											bidSuitToValue.set(
												curTrumpSuit,
												bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curBidCardType)
											);
											lastCardRank = curCardRank;
										} else {
											break;
										}
									}
								}
							}
						}
					}
					// console.log(`SUIT TOTALS: ${CardSuit[curTrumpSuit]}: ${bidSuitToValue.get(curTrumpSuit)}`);
					if (
						highestTrumpCardSuit == -1 || 
						bidSuitToValue.get(curTrumpSuit) > bidSuitToValue.get(highestTrumpCardSuit)
					) {
						highestTrumpCardSuit = curTrumpSuit;
					}
				}
			}

			// check TRUMPS/OTHER
			// calculate T, t, O per each suit as TRUMP
			// total sum will then determine a best TRUMP choice (if >3 total)
			// if that best TRUMP choice is NOT of faceUp card suit, PASS and CHOOSE it in Round2 bidding phase
			for (let i = 0; i < cardsBySuitAT.length; i++) {
				// if suit exists on hand
				if (cardsBySuitAT[i] != undefined) {
					// card suit that will be considered as trump, while we value all 4 suits
					const curTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(i);
					// console.log(`[[${TrumpSuit[curTrumpSuit]}]]`);
					// go through all suits
					for (let j = 0; j < cardsBySuitAT.length; j++) {
						// if suit exists
						if (cardsBySuitAT[j] != undefined) {
							const curCardSuit: TrumpSuit = CardSuitToTrumpSuit(j);
							const suitCardsNT: Card[] = cardsBySuitNT[j];
							const suitCardsAT: Card[] = cardsBySuitAT[j];
							// console.log(`[${TrumpSuit[curCardSuit]}]`);

							// check/add for OTHER_UNBEATABLE
							if (curTrumpSuit != curCardSuit) {
								// check for unbeatable Other
								if (suitCardsNT[0].rank == 7) {
									bidSuitToValue.set(
										curTrumpSuit,
										bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(BidCardType.OTHER_UNBEATABLE)
									);
									// console.log("OTHER_UNBEATABLE");
									// console.log(`+${this.bidCardTypeToBidCardValue.get(BidCardType.OTHER_UNBEATABLE)}`);
								}
							}

							// check/add TRUMP_UNBEATABLE and TRUMP_MINOR
							// curTrumpSuit == curCardSuit
							else {
								let curTrumpType: BidCardType = BidCardType.TRUMP_UNBEATABLE;
								const curCard: Card = suitCardsAT[0];
								const curCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(curCard.suit);
								let lastCardRank: CardRank = curCard.GetRank(TrumpMode.ALL_TRUMPS, curCardSuitAsTrumpSuit);
								if (lastCardRank != 7) {
									// console.log("TRUMP_MINOR");
									curTrumpType = BidCardType.TRUMP_MINOR;
								}
								// else {
								//     console.log("TRUMP_UNBEATABLE");
								// }
								// console.log(`+${this.bidCardTypeToBidCardValue.get(curTrumpType)}`);
								bidSuitToValue.set(
									curTrumpSuit,
									bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curTrumpType)
								);

								if (suitCardsAT.length > 1) {
									for (let k = 1; k < suitCardsAT.length; k++) {
										const curCard: Card = suitCardsAT[k];
										const curCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(curCard.suit);
										const curCardRank: CardRank = curCard.GetRank(TrumpMode.TRUMP_OTHER, curCardSuitAsTrumpSuit);
										if (curTrumpType == BidCardType.TRUMP_UNBEATABLE) {
											if (curCardRank != lastCardRank - 1) {
												// console.log("TRUMP_MINOR");
												curTrumpType = BidCardType.TRUMP_MINOR;
											}
											// else {
											//     console.log("TRUMP_UNBEATABLE");
											// }
										}
										// console.log(`+${this.bidCardTypeToBidCardValue.get(curTrumpType)}`);
										bidSuitToValue.set(
											curTrumpSuit,
											bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curTrumpType)
										);
										lastCardRank = curCardRank;
									}
								}
							}
						}
					}
					// console.log(`SUIT TOTALS: ${CardSuit[curTrumpSuit]}: ${bidSuitToValue.get(curTrumpSuit)}`);
					if (highestTrumpCardSuit == -1 || bidSuitToValue.get(curTrumpSuit) > bidSuitToValue.get(highestTrumpCardSuit)) {
						highestTrumpCardSuit = curTrumpSuit;
					}
				}
			}

			// console.log("RESULTS:");
			// bidSuitToValue.forEach((value: number, bidSuit:TrumpSuit)=>{
			//     console.log(`${TrumpSuit[bidSuit]}: ${value}`);
			// });
			// console.log(`HIGHEST: ${TrumpSuit[highestTrumpCardSuit]}: ${bidSuitToValue.get(highestTrumpCardSuit)}`);

			// if total value of faceUpcard suit is at least as high as total value of highestTrumpCardSuit
			// (eg either its the same suit, or there is another one with the same value)
			const highestTrumpCardSuitValue: number = bidSuitToValue.get(highestTrumpCardSuit);
			const tableCardSuitValue: number = bidSuitToValue.get(tableCardSuitAsTrumpSuit);
			// console.log(playerIndex, tableCardSuitValue+"("+this.minValueForBid+") vs "+tableCardSuitValue);
			if (highestTrumpCardSuitValue == tableCardSuitValue) {
				// prefer faceUpCard suit.
				// if min bid value has been reached
				if (tableCardSuitValue >= this.minValueForBid) {
					this.lastBidChoice.choiceType = PopupBiddingChoiceType.ACCEPT;
					// "h100": "You can bid ${CARD_SUIT}.",
					this.hintText = AppLocalization.GetHintTextById('h100').replace(
						'${CARD_SUIT}',
						locHints.suits[tableCardSuitAsTrumpSuit]
					);
					// console.log(tableCardSuitAsTrumpSuit, TrumpSuit[tableCardSuitAsTrumpSuit], locHints.suits);
				} else {
					// default bidChoice is PASS
					this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
					// "h106": "You should pass.",
					this.hintText = AppLocalization.GetHintTextById('h106');
				}
			}
			// highest trump suit is not faceUp card suit
			else {
				// highest trump suit is enough for a Bid
				if (highestTrumpCardSuitValue >= this.minValueForBid) {
					// bid for the highest suit (in the next bidding round - cant bid in this round)
					this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
					// "h107": "You should pass, then bid ${CARD_SUIT}.",
					this.hintText = AppLocalization.GetHintTextById('h107').replace('${CARD_SUIT}', locHints.suits[highestTrumpCardSuit]);
				}
				// highest trump suit is NOT enough for a Bid
				else {
					// default bidChoice is PASS
					this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
					// "h106": "You should pass.",
					this.hintText = AppLocalization.GetHintTextById('h106');
				}
			}
		}
	}

	/**
     * Choose suit of face-up card?
     * - then pick faceup card
     * @param inputData 
        {
            taskType: TaskTypeBelote.CHOICE_TAKE_SUIT, 
            onComplete: this.OnTaskComplete.bind(this),
            playerIndex: this.data.lastPlayedPlayerIndex,
        }
     */
	public TaskResolveTakeSuit(inputData: any): void {
		inputData.onComplete(inputData.taskType, { choiceType: this.lastBidChoice.choiceType });
	}

	/**
	 * Player or Bot decide what suit to choose based on hand cards etc (or PASS)
	 * @param disabledChoices does contain actual suit(s), unlike @see ResolveTakeSuitChoice
	 *        This also means, that AT/NT are passed inside this variable, if they are not available,
	 *        whereas in case of @see ResolveChooseSuitChoice, they are computed within the function.
	 * @param tableCard Faceup table card.
	 */
	public ResolveChooseSuitChoice(disabledChoices: PopupBiddingChoiceType[], tableCard: Card): void {
		const tableData: TableData = this.table.GetStateMachine().GetData();
		const playerIndex: number = tableData.lastPlayedPlayerIndex;
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		const playerCards: Card[] = player.GetCards();
		// console.log(playerCards.map(card=>card.log()));
		// includes faceUp card
		const allCards: Card[] = playerCards.concat(tableCard);
		// const isNTAT: boolean = false;
		// total suit value accessible by CardSuit key
		let bidSuitToValue: Map<TrumpSuit, number> = new Map();
		let highestTrumpCardSuit: number = -1;
		const locHints: HintsSharedLocData = AppLocalization.GetSharedData().table.hints;
		// const enableNTAT: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.enableNTAT, this.table.variationId);
		// const isNTAT: boolean = enableNTAT && (enableNTAT.value as boolean);
		const isNTAT: boolean = disabledChoices.indexOf(PopupBiddingChoiceType.NOTRUMPS) == -1;
		const tableCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(tableCard.suit);

		// will contain suit cards, sorted each as if each was it's own Trump, downwards
		let cardsBySuitAT: Card[][] = [];
		// same as @see cardsBySuitNT, but used in NT calculation only (OTHER calc can use this too, but we only check for Ace)
		let cardsBySuitNT: Card[][] = [];
		// collect cards by suit
		for (let i: number = 0; i < allCards.length; i++) {
			const card: Card = allCards[i];
			// NOTE: if you ignore disabled suit here, NT and AT will not account for it, and neither will T/O account for iot in value calculations. What you want to do is to do your calculations as if all cards were present, but only do highestTrumpCardSuit calculations for TRUMP suits if calculating for T/O (NT/AT just do all calculations).
			//       - include it first, but after AT/NT check is done, remove it for T/O calculations.
			// const isDisabledSuit: boolean = this.IsDisabledSuit(card.suit, disabledChoices);
			// if (!isDisabledSuit) {
			if (cardsBySuitAT[card.suit] == undefined) {
				cardsBySuitAT[card.suit] = [];
				cardsBySuitNT[card.suit] = [];
				bidSuitToValue.set(CardSuitToTrumpSuit(card.suit), 0);
			}
			cardsBySuitAT[card.suit].push(card);
			cardsBySuitNT[card.suit].push(card);
			// }
		}
		// sort: cards in each suit by rank from highest to lowest
		//       (EACH SUIT ARRAY BY ITS OWN SUIT AS TRUMP SUIT)
		for (let i = 0; i < cardsBySuitAT.length; i++) {
			if (cardsBySuitAT[i] != undefined) {
				let suitCardsAT: Card[] = cardsBySuitAT[i];
				let suitCardsNT: Card[] = cardsBySuitNT[i];
				const cardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(i);
				// console.log(TrumpSuit[cardSuitAsTrumpSuit]);
				// NOTE: order is descending, so 0 rank is Jack (same for AT, AT258, TRUMP)
				suitCardsAT.sort(
					(cardA: Card, cardB: Card) =>
						cardB.GetRank(TrumpMode.ALL_TRUMPS, cardSuitAsTrumpSuit) - cardA.GetRank(TrumpMode.ALL_TRUMPS, cardSuitAsTrumpSuit)
				);
				// NOTE: order is descending, so 0 rank is Ace (same for NT, NT130, OTHER)
				suitCardsNT.sort(
					(cardA: Card, cardB: Card) =>
						cardB.GetRank(TrumpMode.NO_TRUMPS, cardSuitAsTrumpSuit) - cardA.GetRank(TrumpMode.NO_TRUMPS, cardSuitAsTrumpSuit)
				);
			}
		}
		// console.log(cardsBySuitAT.forEach(suitCards=>suitCards.map(card=>card.log())));

		// NT/AT: count hand card values if NT or AT was declared
		if (isNTAT) {
			bidSuitToValue.set(TrumpSuit.NOTRUMPS, 0);
			bidSuitToValue.set(TrumpSuit.ALLTRUMPS, 0);
			// 0=NT; 1=AT
			for (let i = 0; i < 2; i++) {
				let curTrumpSuit: TrumpSuit;
				let curBidCardType: BidCardType;
				let curTrumpMode: TrumpMode;
				if (i == 0) {
					curTrumpSuit = TrumpSuit.NOTRUMPS;
					curBidCardType = BidCardType.NT_AT_UNBEATABLE;
					curTrumpMode = TrumpMode.NO_TRUMPS;
				} else {
					curTrumpSuit = TrumpSuit.ALLTRUMPS;
					curBidCardType = BidCardType.NT_AT_UNBEATABLE;
					curTrumpMode = TrumpMode.ALL_TRUMPS;
				}
				// count hand card values
				for (let j = 0; j < cardsBySuitAT.length; j++) {
					// if suit exists on hand
					if (cardsBySuitAT[j] != undefined) {
						// const cardSuit: CardSuit = CardSuit[j];
						// console.log(CardSuit[j]);
						let suitCards: Card[];
						if (i == 0) {
							suitCards = cardsBySuitNT[j];
						} else {
							suitCards = cardsBySuitAT[j];
						}
						// console.log(i==0?"NT":"AT");

						// starts with leading card
						let curCard: Card = suitCards[0];
						let curCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(curCard.suit);
						let lastCardRank: CardRank = curCard.GetRank(curTrumpMode, curCardSuitAsTrumpSuit);
						if (lastCardRank == 7) { // leading rank highest rank
							// console.log("UNBEATABLE");
							// console.log(`+${this.bidCardTypeToBidCardValue.get(curBidCardType)}`);
							bidSuitToValue.set(
								curTrumpSuit,
								bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curBidCardType)
							);
							// if more cards in this suit
							if (suitCards.length > 1) {
								// count downwards all those cards with rank 1 lower than the previous card
								for (let k = 1; k < suitCards.length; k++) {
									curCard = suitCards[k];
									curCardSuitAsTrumpSuit = CardSuitToTrumpSuit(curCard.suit);
									const curCardRank: CardRank = curCard.GetRank(curTrumpMode, curCardSuitAsTrumpSuit);
									if (curCardRank == lastCardRank - 1) {
										// console.log(`+${this.bidCardTypeToBidCardValue.get(curBidCardType)}`);
										bidSuitToValue.set(
											curTrumpSuit,
											bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curBidCardType)
										);
										lastCardRank = curCardRank;
									} else {
										break;
									}
								}
							}
						}
					}
				}
				// console.log(`SUIT TOTALS: ${TrumpSuit[curTrumpSuit]}: ${bidSuitToValue.get(curTrumpSuit)}`);
				const isDisabledSuit: boolean = this.IsDisabledTrumpSuit(curTrumpSuit, disabledChoices);
				if (
					!isDisabledSuit &&
					(highestTrumpCardSuit == -1 || bidSuitToValue.get(curTrumpSuit) > bidSuitToValue.get(highestTrumpCardSuit))
				) {
					highestTrumpCardSuit = curTrumpSuit;
				}
			}
		}

		// check TRUMPS/OTHER
		// calculate T, t, O per each suit as TRUMP
		// total sum will then determine a best TRUMP choice (if >3 total)
		// if that best TRUMP choice if NOT of faceUp card suit, PASS and CHOOSE it in Round2 bidding phase
		for (let i = 0; i < cardsBySuitAT.length; i++) {
			// if suit exists on hand
			if (cardsBySuitAT[i] != undefined) {
				// card suit that will be considered as trump, while we value all 4 suits
				const curTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(i);
				// console.log(`[[${TrumpSuit[curTrumpSuit]}]]`);
				// go through all suits
				for (let j = 0; j < cardsBySuitAT.length; j++) {
					// if suit exists
					if (cardsBySuitAT[j] != undefined) {
						const curCardSuit: TrumpSuit = CardSuitToTrumpSuit(j);
						const suitCardsNT: Card[] = cardsBySuitNT[j];
						const suitCardsAT: Card[] = cardsBySuitAT[j];
						// console.log(`[${TrumpSuit[curCardSuit]}]`);

						// check/add for OTHER_UNBEATABLE
						if (curTrumpSuit != curCardSuit) {
							// check for unbeatable Other
							if (suitCardsNT[0].rank == 7) {
								bidSuitToValue.set(
									curTrumpSuit,
									bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(BidCardType.OTHER_UNBEATABLE)
								);
								// console.log("OTHER_UNBEATABLE");
								// console.log(`+${this.bidCardTypeToBidCardValue.get(BidCardType.OTHER_UNBEATABLE)}`);
							}
						}

						// check/add TRUMP_UNBEATABLE and TRUMP_MINOR
						// curTrumpSuit == curCardSuit
						else {
							let curTrumpType: BidCardType = BidCardType.TRUMP_UNBEATABLE;
							const curCard: Card = suitCardsAT[0];
							const curCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(curCard.suit);
							let lastCardRank: CardRank = curCard.GetRank(TrumpMode.ALL_TRUMPS, curCardSuitAsTrumpSuit);
							if (lastCardRank != 7) {
								// console.log("TRUMP_MINOR");
								curTrumpType = BidCardType.TRUMP_MINOR;
							}
							// else {
							//     console.log("TRUMP_UNBEATABLE");
							// }
							// console.log(`+${this.bidCardTypeToBidCardValue.get(curTrumpType)}`);
							bidSuitToValue.set(
								curTrumpSuit,
								bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curTrumpType)
							);

							if (suitCardsAT.length > 1) {
								for (let k = 1; k < suitCardsAT.length; k++) {
									const curCard: Card = suitCardsAT[k];
									const curCardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(curCard.suit);
									const curCardRank: CardRank = curCard.GetRank(TrumpMode.TRUMP_OTHER, curCardSuitAsTrumpSuit);
									if (curTrumpType == BidCardType.TRUMP_UNBEATABLE) {
										if (curCardRank != lastCardRank - 1) {
											// console.log("TRUMP_MINOR");
											curTrumpType = BidCardType.TRUMP_MINOR;
										}
										// else {
										//     console.log("TRUMP_UNBEATABLE");
										// }
									}
									// console.log(`+${this.bidCardTypeToBidCardValue.get(curTrumpType)}`);
									bidSuitToValue.set(
										curTrumpSuit,
										bidSuitToValue.get(curTrumpSuit) + this.bidCardTypeToBidCardValue.get(curTrumpType)
									);
									lastCardRank = curCardRank;
								}
							}
						}
					}
				}
				// console.log(`SUIT TOTALS: ${CardSuit[curTrumpSuit]}: ${bidSuitToValue.get(curTrumpSuit)}`);
				const isDisabledSuit: boolean = this.IsDisabledTrumpSuit(curTrumpSuit, disabledChoices);
				if (
					!isDisabledSuit &&
					(highestTrumpCardSuit == -1 || bidSuitToValue.get(curTrumpSuit) > bidSuitToValue.get(highestTrumpCardSuit))
				) {
					highestTrumpCardSuit = curTrumpSuit;
				}
			}
		}

		// console.log("RESULTS:");
		// bidSuitToValue.forEach((value: number, bidSuit:TrumpSuit)=>{
		//     console.log(`${TrumpSuit[bidSuit]}: ${value}`);
		// });
		// console.log(`HIGHEST: ${TrumpSuit[highestTrumpCardSuit]}: ${bidSuitToValue.get(highestTrumpCardSuit)}`);

		// NOTE: highestTrumpCardSuit is TrumpSuit
		const highestTrumpCardSuitValue: number = bidSuitToValue.get(highestTrumpCardSuit);
		// highest trump suit is enough for a Bid
		if (highestTrumpCardSuitValue >= this.minValueForBid) {
			// bid for the highest suit
			this.lastBidChoice.choiceType = TrumpSuitToPopupBiddingChoiceType(highestTrumpCardSuit);
			// "h100": "You can bid ${CARD_SUIT}.",
			this.hintText = AppLocalization.GetHintTextById('h100').replace('${CARD_SUIT}', locHints.suits[highestTrumpCardSuit]);
		}
		// highest trump suit is NOT enough for a Bid
		else {
			// if PASS is NOT allowed
			if (disabledChoices.indexOf(PopupBiddingChoiceType.PASS) != -1) {
				// bid for the highest suit (though it is lower than minimum needed for Bid)
				this.lastBidChoice.choiceType = TrumpSuitToPopupBiddingChoiceType(highestTrumpCardSuit);
				// "h100": "You can bid ${CARD_SUIT}.",
				this.hintText = AppLocalization.GetHintTextById('h100').replace('${CARD_SUIT}', locHints.suits[highestTrumpCardSuit]);
			}
			// if PASS IS allowed
			else {
				// default bidChoice is PASS
				this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
				// "h106": "You should pass.",
				this.hintText = AppLocalization.GetHintTextById('h106');
			}
		}
	}

	/**
     * Choose your own suit
     * - then take faceup card
     * @param inputData 
        {
            taskType: TaskTypeBelote.CHOICE_SELECT_SUIT, 
            onComplete: this.OnTaskComplete.bind(this),
            playerIndex: this.data.lastPlayedPlayerIndex,
        }
     */
	public TaskResolveChooseSuit(inputData: any): void {
		inputData.onComplete(inputData.taskType, { choiceType: this.lastBidChoice.choiceType });
	}

	/**
	 * To set lastBidChoice manually from player's bid popup selection
	 * @param bidChoice
	 */
	public SetLastBidChoice(bidChoice: ContraBidChoice): void {
		this.lastBidChoice = {
			choiceType: bidChoice.choiceType,
			choiceValueIndex: bidChoice.choiceValueIndex,
		};
	}

	/**
	 * Coinche & Contree only
	 * Provides text hint to player during bidding phase
	 * Determines bid choice for BOT during bidding phase
	 *
	 * NOTE that if this runs for player, it wont actually make a choice for player, but it will update this.lastBidChoice, which needs to be additionally overriden by player choice @see this.SetLastBidChoice
	 * @param disabledChoices NOTE: CardSuit-s can NOT be disabled in Coinche/Contree modes (only NT/AT can be disabled).
	 * @returns
	 */
	public ResolveMakeBidChoice(disabledChoices: PopupBiddingChoiceType[]): void {
		// console.log("ResolveMakeBidChoice");
		// console.log("tableData.bids.highestChoiceValueIndex", this.table.GetStateMachine().GetData().bids.highestChoiceValueIndex);
		// console.log("this.lastBidChoice.choiceValueIndex", this.lastBidChoice.choiceValueIndex);

		const tableData: TableData = this.table.GetStateMachine().GetData();
		const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
		const playerIndex: number = tableData.lastPlayedPlayerIndex;
		const teammateIndex: number = tableStateMachine.GetFixedPlayerIndex(tableData.lastPlayedPlayerIndex + 2);
		const startChoiceValueIndex: number = tableData.bids.highestChoiceValueIndex + 1;
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		const playerCards: Card[] = player.GetCards();
		const isEnableNTAT: boolean = disabledChoices.indexOf(PopupBiddingChoiceType.NOTRUMPS) == -1;
		// highest bid choice is at CAPOT or GENERALE
		// made by opposing team
		const sliderData = AppLocalization.GetVariationData().table.bidPopup.slider;
		const highestChoiceValueIndex: number = tableData.bids.highestChoiceValueIndex;
		const highestChoiceType : TrumpSuit = tableData.bids.highestChoiceType;
		const declarationsCountForContractWinLoss: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.declarationsCountForContractWinLoss, this.table.variationId);
		const isDeclarationsCountForContractWinLoss: boolean = declarationsCountForContractWinLoss && (declarationsCountForContractWinLoss.value as boolean);
		
		// only consider declarations, if they account for win
		// NOTE: trumpSuit: only affects values of decls in certain TrumpSuit cases (actual sequence for ex doesnt matter what color, but value of sequence might differ for diff trumpSuits)
		const GetDeclsAdditiveBidValueIndex = (trumpSuit: TrumpSuit) => {
			if (!isDeclarationsCountForContractWinLoss) {
				return 0;
			}
			// get bids value for this player
			const bidsValue = this.table.declarationsManager.GetPlayerDeclarationsValue(playerIndex, trumpSuit);
			let extraIndexesFromDecls: number;
			const aiLevel = this.table.GetTableAI().GetAILevel();
			switch(aiLevel) {
				case AILevel.BEGINNER:
 					extraIndexesFromDecls = Math.round(0.2 * bidsValue / 10);
					break;
				case AILevel.INTERMEDIATE:
 					extraIndexesFromDecls = Math.round(0.4 * bidsValue / 10);
					break;
				case AILevel.EXPERT:
 					extraIndexesFromDecls = Math.round(0.6 * bidsValue / 10);
					break;
			}
			// console.log(
			// 	playerIndex, 
			// 	AILevel[aiLevel], 
			// 	TrumpSuit[trumpSuit], 
			// 	"extraIndexesFromDecls("+Math.round(bidsValue / 10)+"/"+extraIndexesFromDecls+")"
			// );

			return extraIndexesFromDecls;
		}

		// cap maximum bid value index, so that it doesnt reach Capot. 
		// Capot&Generale are governed by different bidding rules.
		const CapChoiceValueIndex = (uncappedIndex: number) => {
			return Math.min(sliderData.capot.choiceIndex - 1, uncappedIndex);
		}
							
		// this.table.GetTableAI().SetLastBidChoice(bidChoice);
		// console.log("highestChoiceValueIndex",tableData.bids?.highestChoiceValueIndex);

		// declarations
		// test: when these get assigned
		// result: post bidding phase, as they are played; TOO LATE to take from during bidding phase;
		// console.log(
		// 	tableData.dealMajorDeclarationPointsUs,
		// 	tableData.dealBelotePointsUs,
		// 	tableData.dealMajorDeclarationPointsThem,
		// 	tableData.dealBelotePointsThem,
		// );
		
		// // DEBUG: bot COINCHES live Player
		// const curSide: PlayerSide = GetPlayerSide(playerIndex);
		// const playerSide: PlayerSide = GetPlayerSide(Players.PLAYER_INDEX);
		// if (tableData.bids.highestChoicePlayerIndex == Players.PLAYER_INDEX) {
		//     // console.log(curSide, playerSide)
		//     if (curSide != playerSide) {
		//         this.lastBidChoice.choiceType = PopupBiddingChoiceType.CONTRA_RECONTRA;
		//         this.lastBidChoice.choiceValueIndex = -1;
		//         return;
		//     }
		// }

		// TODO: only can play generale, if enabled in Options
		// const allowGenerale: boolean = (OptionsDataManager.GetSharedData().itemsById[OptionsItemKeys.allowGenerale].value as boolean);

		// cardsBySuit
		let cardsBySuit: Card[][] = [];
		for (let i: number = 0; i < playerCards.length; i++) {
			const card: Card = playerCards[i];
			if (cardsBySuit[card.suit] == undefined) {
				cardsBySuit[card.suit] = [];
			}
			cardsBySuit[card.suit].push(card);
		}
		// sort cardsBySuit by number of cards in suit (highest count at index 0)
		// EDIT: this messes up access by suit eg cardsBySuit[CardSuit.DIAMONDS] is now all HEARTS etc.
		// cardsBySuit.sort((suitCardsA, suitCardsB) => {
		//     return suitCardsB.length - suitCardsA.length;
		// });
		// console.log(cardsBySuit.map(suitCards=>{return suitCards.length}));

		// determine first teammates value bid (first value bid)
		// NOTE: playerBid.bidChoice.choiceValueIndex here during a PASS traces previous highest value choice (UNUSABLE),
		//       BUT playerBid.bidChoice.choiceType actually does trace a PASS correctly (well use this)
		let firstTeammateValueBid: ContraBidChoice = null;
		for (let i = 0; i < tableData.bids.history.length; i++) {
			const playerBid = tableData.bids.history[i];
			if (playerBid.playerIndex == teammateIndex) {
				// teammate value bid (suits|AT|NT)
				if (playerBid.bidChoice.choiceType < PopupBiddingChoiceType.CONTRA_RECONTRA) {
					firstTeammateValueBid = playerBid.bidChoice;
					break;
				}
			}
		}
		// console.log('firstTeammateValueBid:', firstTeammateValueBid);

		// determine highest teammate's value bid (last value bid)
		// NOTE: playerBid.bidChoice.choiceValueIndex here during a PASS traces previous highest value choice (UNUSABLE),
		//       BUT playerBid.bidChoice.choiceType actually does trace a PASS correctly (well use this)
		let highestTeammateValueBid: ContraBidChoice = null;
		for (let i = tableData.bids.history.length - 1; i >= 0; i--) {
			const playerBid = tableData.bids.history[i];
			if (playerBid.playerIndex == teammateIndex) {
				// teammate value bid (suits|AT|NT)
				if (playerBid.bidChoice.choiceType < PopupBiddingChoiceType.CONTRA_RECONTRA) {
					highestTeammateValueBid = playerBid.bidChoice;
					break;
				}
			}
		}
		// console.log('highestTeammateValueBid:', highestTeammateValueBid);

		// only runs if first to bid (no trumpSuit) or second to bid (first player's trumpSuit) player 
		// has no cards to increase bet index value (so no actual cards to bid with),
		// but does have an exceedingly high declarations value on hand;
		// tests for every trumpSuit type (not much difference overall tho)
		// if trumpSuit=undefined, first player bids. Test all TrumpSuits. 
		// Else try followup 1st player's bid suit.
		// We (further on top of aiLevel neutering) neuter the strength of such declaration values.
		const AddExceedinglyHighDeclarations = (trumpSuit: TrumpSuit = TrumpSuit.UNDEFINED)=>{
			if (trumpSuit == TrumpSuit.UNDEFINED) {
				let highestTrumpSuit: TrumpSuit;
				let highestDeclValueIndex: number = 0;
				for (const value of Object.values(TrumpSuit)) {
					if(Number.isInteger(value)) {
						const declValueIndex = GetDeclsAdditiveBidValueIndex(value as TrumpSuit);
						if (highestDeclValueIndex < declValueIndex) {
							highestDeclValueIndex = declValueIndex;
							highestTrumpSuit = value as TrumpSuit;
						}
					}
				}
				highestDeclValueIndex = Math.floor(highestDeclValueIndex * 0.75);
				if (highestDeclValueIndex > 0) {
					if (startChoiceValueIndex <= highestDeclValueIndex) {
						this.lastBidChoice.choiceType = TrumpSuitToPopupBiddingChoiceType(highestTrumpSuit);
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(highestDeclValueIndex);
						this.ResolveContraBidHintText();
						return;
					}
				}
			}
			// else teammate already bid something, add on top of that 
			// (if less than highest [eg enemy couldve overbid])
			else {
				let declValueIndex = GetDeclsAdditiveBidValueIndex(trumpSuit);
				declValueIndex = Math.floor(declValueIndex * 0.75);
				const cappedIndex = CapChoiceValueIndex(firstTeammateValueBid.choiceValueIndex + declValueIndex);
				if (highestChoiceValueIndex < cappedIndex) {
					this.lastBidChoice.choiceType = TrumpSuitToPopupBiddingChoiceType(trumpSuit);
					this.lastBidChoice.choiceValueIndex = cappedIndex;
					this.ResolveContraBidHintText();
					return;
				}
			}
		}

		// number of unique teammate's value bids (not PASS, CONTRA etc)
		// we use this to determine when to add player's card values to teammate's bid (should only happen once)
		// NOTE: playerBid.bidChoice.choiceValueIndex here during a PASS traces previous highest value choice (UNUSABLE),
		//       BUT playerBid.bidChoice.choiceType actually does trace a PASS correctly (well use this)
		let numTeammateValueBids: number = 0;
		let numPlayerValueBids: number = 0;
		tableData.bids.history.forEach((playerBid: PlayerCoincheBidChoice) => {
			if (playerBid.playerIndex == teammateIndex) {
				// teammate value bid (suits|AT|NT)
				if (playerBid.bidChoice.choiceType < PopupBiddingChoiceType.CONTRA_RECONTRA) {
					numTeammateValueBids++;
				}
			}
			if (playerBid.playerIndex == playerIndex) {
				if (playerBid.bidChoice.choiceType < PopupBiddingChoiceType.CONTRA_RECONTRA) {
					numPlayerValueBids++;
				}
			}
		});
		// console.log(`${playerIndex}->${teammateIndex} ${numTeammateValueBids}`);
		
		// Capot & Generale Contra resolution
		// if Capot|Generale contract called
		if (startChoiceValueIndex >= sliderData.capot.choiceIndex) {
			// NOTE: PlayerSide.US and PlayerSide.THEM shouldnt be used for comparison (because if enemy bot runs this, it will give it PlayerSide.THEM, however it would prob expect PlayerSide.US still);
			// but variables can be used for comparisons eg attackingSide == playerSide
			const highestChoicePlayerSide = GetPlayerSide(tableData.bids.highestChoicePlayerIndex);
			const playerSide = GetPlayerSide(playerIndex);
			// console.log(playerIndex, tableData.bids.highestChoicePlayerIndex)
			// if opposite side made Generale or Capot choice
			if(playerSide != highestChoicePlayerSide) {
				let match: Card;
				// Gen|Cap on AT
				if (highestChoiceType == TrumpSuit.ALLTRUMPS) {
					match = playerCards.find(card=>card.rank == CardRank.JACK);
				}
				// Gen|Cap on NT
				else if(highestChoiceType == TrumpSuit.NOTRUMPS) {
					match = playerCards.find(card=>card.rank == CardRank.ACE);
				}
				// else Gen|Cap on ♠|♣|♦|♥
				else {
					match = playerCards.find((card)=>{
						if(highestChoiceType == CardSuitToTrumpSuit(card.suit)) {
							return card.rank == CardRank.JACK;
						}
						else {
							return card.rank == CardRank.ACE;
						}
					});
				}
				// if found matching unbeatable (O|T), CONTRA.
				if (match) {
					this.lastBidChoice.choiceType = PopupBiddingChoiceType.CONTRA_RECONTRA;
					this.lastBidChoice.choiceValueIndex = -1; // 0 => 80
					this.ResolveContraBidHintText();
					return;
				}
			}
		}
		
		// If teammate didnt make a bid
		// > This is FIRST bid OUR PlayerSide is making
		// if (highestTeammateValueBid == null) {
		if (numTeammateValueBids == 0 && numPlayerValueBids == 0) {
			// if bid value check is done for Bid value greater or equal to startChoiceIndex
			// value: 100; valueIndex: 2
			if (startChoiceValueIndex <= this.BidValueToBidValueIndex('100')) {
				if (isEnableNTAT) {
					// Bid: 100 AT
					// If: have 4 Jacks
					let numJacks = 0;
					playerCards.forEach((card) => {
						if (card.rank == CardRank.JACK) numJacks++;
					});
					if (numJacks == 4) {
						// console.log("4 JACKS = BID 100-AT");
						const declValueIndex = GetDeclsAdditiveBidValueIndex(TrumpSuit.ALLTRUMPS);
						this.lastBidChoice.choiceType = PopupBiddingChoiceType.ALLTRUMPS;
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(2 + declValueIndex); // 2 => 100
						this.ResolveContraBidHintText();
						return;
					}
					// Bid: 100 NT
					// If: have 4 Aces
					let numAces = 0;
					playerCards.forEach((card) => {
						if (card.rank == CardRank.ACE) numAces++;
					});
					if (numAces == 4) {
						// console.log("4 ACES = BID 100-NT");
						const declValueIndex = GetDeclsAdditiveBidValueIndex(TrumpSuit.NOTRUMPS);
						this.lastBidChoice.choiceType = PopupBiddingChoiceType.NOTRUMPS;
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(2 + declValueIndex); // 2 => 100
						this.ResolveContraBidHintText();
						return;
					}
				}
				// Bid: 100 Suit
				// If: have Jack, Nine, and 2 other suit cards
				// cardsBySuit.forEach((suitCards: Card[], cardSuit: CardSuit)=>{
				for (let cardSuit = 0; cardSuit < cardsBySuit.length; cardSuit++) {
					const trumpSuit = CardSuitToTrumpSuit(cardSuit);
					const suitCards: Card[] = cardsBySuit[cardSuit];
					if (suitCards?.length >= 4) {
						let hasSuitJack: boolean = false;
						let hasSuitNine: boolean = false;
						suitCards.forEach((card) => {
							if (card.rank == CardRank.JACK) hasSuitJack = true;
							if (card.rank == CardRank.NINE) hasSuitNine = true;
						});
						if (hasSuitJack && hasSuitNine) {
							// console.log("Strong 4-card suit (incl Jack and Nine");
							const trumpSuit = CardSuitToTrumpSuit(cardSuit);
							const declValueIndex = GetDeclsAdditiveBidValueIndex(trumpSuit);
							this.lastBidChoice.choiceType = CardSuitToPopupBiddingChoiceType(cardSuit);
							this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(2 + declValueIndex); // 2 => 100
							this.ResolveContraBidHintText();
							return;
						}
					}
				}
			}
			// value: 90; valueIndex: 1
			if (startChoiceValueIndex <= this.BidValueToBidValueIndex('90')) {
				if (isEnableNTAT) {
					// Bid: 90 AT
					// If: have 3 Jacks (simplified: removed: 'unless an 80 has been bid before')
					let numJacks = 0;
					playerCards.forEach((card) => {
						if (card.rank == CardRank.JACK) numJacks++;
					});
					if (numJacks == 3) {
						// console.log("3 JACKS = BID 90-AT");
						const declValueIndex = GetDeclsAdditiveBidValueIndex(TrumpSuit.ALLTRUMPS);
						this.lastBidChoice.choiceType = PopupBiddingChoiceType.ALLTRUMPS;
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(1 + declValueIndex); // 1 => 90
						this.ResolveContraBidHintText();
						return;
					}
					// Bid: 90 NT
					// If: have 3 Aces (simplified: removed: 'unless an 80 has been bid before')
					let numAces = 0;
					playerCards.forEach((card) => {
						if (card.rank == CardRank.ACE) numAces++;
					});
					if (numAces == 3) {
						// console.log("3 ACES = BID 90-NT");
						const declValueIndex = GetDeclsAdditiveBidValueIndex(TrumpSuit.NOTRUMPS);
						this.lastBidChoice.choiceType = PopupBiddingChoiceType.NOTRUMPS;
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(1 + declValueIndex); // 1 => 90
						this.ResolveContraBidHintText();
						return;
					}
				}
			}
			// value: 80; valueIndex: 0
			if (startChoiceValueIndex <= this.BidValueToBidValueIndex('80')) {
				if (isEnableNTAT) {
					// Bid: 80 AT
					// If: have 2 Jacks
					let numJacks = 0;
					playerCards.forEach((card) => {
						if (card.rank == CardRank.JACK) numJacks++;
					});
					if (numJacks == 2) {
						// console.log("2 JACKS = BID 80-AT");
						const declValueIndex = GetDeclsAdditiveBidValueIndex(TrumpSuit.ALLTRUMPS);
						this.lastBidChoice.choiceType = PopupBiddingChoiceType.ALLTRUMPS;
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(0 + declValueIndex); // 0 => 80
						this.ResolveContraBidHintText();
						return;
					}
					// Bid: 80 NT
					// If: have 2 Aces
					let numAces = 0;
					playerCards.forEach((card) => {
						if (card.rank == CardRank.ACE) numAces++;
					});
					if (numAces == 2) {
						const declValueIndex = GetDeclsAdditiveBidValueIndex(TrumpSuit.NOTRUMPS);
						// console.log("2 ACES = BID 80-NT");
						this.lastBidChoice.choiceType = PopupBiddingChoiceType.NOTRUMPS;
						this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(0 + declValueIndex); // 0 => 80
						this.ResolveContraBidHintText();
						return;
					}
				}
				// Bid: 80 Suit
				// If: have Jack OR Nine (not both)
				// cardsBySuit.forEach((suitCards: Card[], cardSuit: CardSuit)=>{
				for (let cardSuit = 0; cardSuit < cardsBySuit.length; cardSuit++) {
					const suitCards: Card[] = cardsBySuit[cardSuit];
					if (suitCards) {
						let hasSuitJack: boolean = false;
						let hasSuitNine: boolean = false;
						suitCards.forEach((card) => {
							if (card.rank == CardRank.JACK) hasSuitJack = true;
							if (card.rank == CardRank.NINE) hasSuitNine = true;
						});
						if (hasSuitJack || hasSuitNine) {
							const trumpSuit = CardSuitToTrumpSuit(cardSuit);
							const declValueIndex = GetDeclsAdditiveBidValueIndex(trumpSuit);
							// console.log(playerIndex, "bid "+CardSuit[cardSuit]);
							this.lastBidChoice.choiceType = CardSuitToPopupBiddingChoiceType(cardSuit);
							this.lastBidChoice.choiceValueIndex = CapChoiceValueIndex(0 + declValueIndex); // 0 => 80
							this.ResolveContraBidHintText();
							return;
						}
					}
				}
			}

			AddExceedinglyHighDeclarations();
		}
		// If teammate already made a value bid (not PASS)
		// - rules that apply after teammate's Nth (1,2,..) value bid
		// EDIT: only increase bid once (should not add same card points multiple times!)
		// else {
		else if (numTeammateValueBids == 1 && numPlayerValueBids == 0) {
			const trumpSuit = PopupBiddingChoiceTypeToTrumpSuit(firstTeammateValueBid.choiceType);
			// first and last teammate's value bids (if exist) of same suit (this can also be the same bids)
			// - bottom line: teammate not switching suits; else PASS;
			if (firstTeammateValueBid.choiceType === highestTeammateValueBid.choiceType) {
				const sliderData = AppLocalization.GetVariationData().table.bidPopup.slider;
				// if bid choice is below Capot
				// if (startChoiceValueIndex < sliderData.capot.choiceIndex) {
					const isNTAT =
						firstTeammateValueBid.choiceType == PopupBiddingChoiceType.NOTRUMPS ||
						firstTeammateValueBid.choiceType == PopupBiddingChoiceType.ALLTRUMPS;
					// teammate bid suit: HEARTS|DIAMONDS|SPADES|CLUBS
					if (!isNTAT) {
						// check curPlayer hand cards
						// [+] +10 for Jack of teammates suit (except if NT)
						// [+] +10 for 9 of teammates suit (except if NT)

						// If: teammate bid 80
						if (firstTeammateValueBid.choiceValueIndex == 0) {
							// teammate's value bid's card suit
							const teammateBidCardSuit: CardSuit | null = PopupBiddingChoiceTypeToCardSuit(firstTeammateValueBid.choiceType);
							// if current player has cards of the same suit that teammate's value bid suit
							if (cardsBySuit[teammateBidCardSuit]) {
								// only bid in this suit, if got Jack or 9 in it
								const hasNine = this.IsCardInCollection(cardsBySuit[teammateBidCardSuit], teammateBidCardSuit, CardRank.JACK);
								const hasJack = this.IsCardInCollection(cardsBySuit[teammateBidCardSuit], teammateBidCardSuit, CardRank.NINE);
								let addToValueIndex: number = 0;
								if (hasNine) {
									addToValueIndex++;
								}
								if (hasJack) {
									addToValueIndex++;
								}
								if (addToValueIndex > 0) {
									const declValueIndex = GetDeclsAdditiveBidValueIndex(trumpSuit);
									const cappedIndex = CapChoiceValueIndex(firstTeammateValueBid.choiceValueIndex + addToValueIndex + declValueIndex);
									if (highestChoiceValueIndex < cappedIndex) {
										this.lastBidChoice.choiceType = firstTeammateValueBid.choiceType;
										this.lastBidChoice.choiceValueIndex = cappedIndex;
										this.ResolveContraBidHintText();
										return;
									}
									else {
										this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
										this.lastBidChoice.choiceValueIndex = tableData.bids.highestChoiceValueIndex;
										this.ResolveContraBidHintText();
										return;
									}
								}
							}
						}
					}
					// else teammate bid ALLTRUMPS|NOTRUMPS
					else {
						// check curPlayer hand cards
						// NT/AT: +10 for each 'master' card (NT master card: A; AT master card: J)
						let addToValueIndex: number = 0;
						if (firstTeammateValueBid.choiceType == PopupBiddingChoiceType.NOTRUMPS) {
							playerCards.forEach((card) => {
								if (card.rank == CardRank.ACE) addToValueIndex++;
							});
						} else if (firstTeammateValueBid.choiceType == PopupBiddingChoiceType.ALLTRUMPS) {
							playerCards.forEach((card) => {
								if (card.rank == CardRank.JACK) addToValueIndex++;
							});
						}
						const declValueIndex = GetDeclsAdditiveBidValueIndex(trumpSuit);
						const cappedIndex = CapChoiceValueIndex(firstTeammateValueBid.choiceValueIndex + addToValueIndex + declValueIndex);
						// if (addToValueIndex > 0 || declValueIndex > 0) {
							if (highestChoiceValueIndex < cappedIndex) {
								this.lastBidChoice.choiceType = firstTeammateValueBid.choiceType;
								this.lastBidChoice.choiceValueIndex = cappedIndex;
								this.ResolveContraBidHintText();
								return;
							}
							else {
								this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
								this.lastBidChoice.choiceValueIndex = tableData.bids.highestChoiceValueIndex;
								this.ResolveContraBidHintText();
								return;
							}
						// }
					}
				// }
			}
			
			AddExceedinglyHighDeclarations(trumpSuit);
		}

		// default: PASS
		this.lastBidChoice.choiceType = PopupBiddingChoiceType.PASS;
		this.lastBidChoice.choiceValueIndex = tableData.bids.highestChoiceValueIndex;
		this.ResolveContraBidHintText();
	}

	/**
	 * Make Coinche (other?) Bid choice for a bot.
	 */
	public TaskResolveMakeBid(inputData: any): void {
		// console.log("TaskResolveMakeBid", PopupBiddingChoiceType[this.lastBidChoice.choiceType]);
		inputData.onComplete(inputData.taskType, { bidChoice: this.lastBidChoice });
	}

	/**
	 * If its live Player's turn, resolve bid hint text (set hintText) for Coinche|Contree
	 * @param bidChoice
	 */
	protected ResolveContraBidHintText(): void {
		const tableData: TableData = this.table.GetStateMachine().GetData();
		const playerIndex: number = tableData.lastPlayedPlayerIndex;
		// only applies to live player. Bots do not use hint bubble popup.
		if (playerIndex == Players.PLAYER_INDEX) {
			// CONTRA
			if (this.lastBidChoice.choiceType == PopupBiddingChoiceType.CONTRA_RECONTRA) {
				// if coinche already happened previously
				if (tableData.contraPlayerIndex != -1) {
					const sRecontra: string = CapitalizeFirstLetter(AppLocalization.GetVariationData().table.declarations.recontra, true);
					this.hintText = AppLocalization.GetHintTextById('h105').replace('${RECONTRA}', sRecontra);
				} else {
					const sContra: string = CapitalizeFirstLetter(AppLocalization.GetVariationData().table.declarations.contra, true);
					this.hintText = AppLocalization.GetHintTextById('h104').replace('${CONTRA}', sContra);
				}
			}
			// PASS
			else if (this.lastBidChoice.choiceType == PopupBiddingChoiceType.PASS) {
				// "h106": "You should pass.",
				this.hintText = AppLocalization.GetHintTextById('h106');
			}
			// SUIT|NT|AT
			else {
				const sliderData = AppLocalization.GetVariationData().table.bidPopup.slider;
				const bidValue: string = sliderData.bubbleDeclarations[this.lastBidChoice.choiceValueIndex];
				// if bid choice is capot or generale
				if (this.lastBidChoice.choiceValueIndex >= sliderData.capot.choiceIndex) {
					// do not mention bid card suit
					// "h103": "You should bid ${BID_VALUE}.",
					this.hintText = AppLocalization.GetHintTextById('h103').replace('${BID_VALUE}', bidValue);
				}
				// if bid choice is below Capot (and Generale)
				else {
					const bidChoiceTypeAsTrumpSuit: TrumpSuit = PopupBiddingChoiceTypeToTrumpSuit(this.lastBidChoice.choiceType);
					const locHints: HintsSharedLocData = AppLocalization.GetSharedData().table.hints;
					// also mention bid card suit
					// "h102": "You should bid ${BID_VALUE} on ${CARD_SUIT}.",
					this.hintText = AppLocalization.GetHintTextById('h102')
						.replace('${BID_VALUE}', bidValue)
						.replace('${CARD_SUIT}', locHints.suits[bidChoiceTypeAsTrumpSuit]);
				}
			}
		}
	}

	/**
	 * Highest AI bettor was COINCHED.
	 * Let that bettor now decide whether to SURCOINCHE or not.
	 */
	public TaskResolveRecontraChoice(inputData: any): void {
		// console.log("resolving recontra (surcoinche|redouble|..)");

		// % chance to re-contra
		let hasPassed: boolean = Math.random() > 0.5;
		// hasPassed = false;
		// if RECONTRA-ed
		if (hasPassed) {
			inputData.onComplete(inputData.taskType, {
				choiceType: PopupBinaryChoiceType.SECONDARY, // PASS
				choiceValueIndex: -1,
			});
		} else {
			inputData.onComplete(inputData.taskType, {
				choiceType: PopupBinaryChoiceType.PRIMARY, // RECONTRA (Surcoinche|Redouble?)
				choiceValueIndex: -1,
			});
		}
	}

	/**
     * BOTS only (not live Player)
     * Resolve which card to play this turn, 
     * Then PLAY said card
     * NOTE: @see GetPlayableCards used by both the AI bot and the player
     * 
        inputData:
            {
                taskType: TaskTypeBelote.RESOLVE_PLAY_CARD, 
                onComplete: this.OnTaskComplete.bind(this),
                playerIndex: curPlayerIndex,
            }
        return: 
            {
                card: Card
            }
    */
	public TaskResolvePlayCard(inputData: any): void {
		// console.log("TableAIBelote::TaskResolvePlayCard", inputData);
		const taskType: number = inputData.taskType;
		const onComplete: Function = inputData.onComplete;
		const playerIndex: number = inputData.playerIndex;
		const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
		const aiLevel: AILevel = tableStateMachine.GetData().options.aiLevel;

		this.ResolvePlayableAndSuggestedCards(playerIndex);
		// console.log("playerIndex=",playerIndex, "playableCards=",this.playableCards, "suggestedCards=",this.suggestedCards);
		

		// play card suggested for bot to play (botPlayCard),
		// or if nothing is suggested, play based on aiLevel:
		// > EASY plays consistently same way
		// > MEDIUM | EXPORT plays randomly within limits
		let playCard: Card;
		switch (aiLevel) {
			case AILevel.BEGINNER:
				playCard = Phaser.Utils.Array.GetRandom(this.playableCards);
				// console.log(this.playableCards.map(c=>c.log()));
				// console.log(playCard.log());
				break;
			case AILevel.INTERMEDIATE:
			case AILevel.EXPERT:
				playCard = this.botPlayCard ?? Phaser.Utils.Array.GetRandom(this.suggestedCards);
				break;
		}

		onComplete(taskType, {
			card: playCard,
		});
	}

	/**
	 * declare belote (if applicable)
	 * @param lastPlayerIndex
	 * @param onComplete
	 */
	public ResolveMinorDeclarationsAfterPlayerPlayedCard(lastPlayerIndex: number, onComplete: Function): void {
		// console.log("ResolveMinorDeclarationsAfterPlayerPlayedCard");

		const card: Card = this.table.tableStackPlay.GetLast();
		const cardRank: CardRank = card.rank;
		const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
		if (cardRank == CardRank.KING || cardRank == CardRank.QUEEN) {
			const trumpSuit: TrumpSuit = tableStateMachine.GetData().trumpSuit;
			const cardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(card.suit);
			// ALLTRUMPS 4 belote/rebelote possible, but only if beloteRebeloteAtAllTrumps=TRUE
			// EDIT: enough to have a complex check during collection @see DeclarationsManager.GetPlayerDeclarationsBelote
			if (trumpSuit == TrumpSuit.ALLTRUMPS || cardSuitAsTrumpSuit == trumpSuit) {
				// get BELOTE declarations by playerIndex (should have one)
				const declarations: PlayerDeclaration[] = tableStateMachine.GetData().declarations;
				const playerBeloteDeclarations: PlayerDeclaration[] = declarations.filter(
					(d) => d.declarationType == DeclarationType.BELOTE_REBELOTE && d.playerIndex == lastPlayerIndex
				);
				if (playerBeloteDeclarations.length > 0) {
					let decIndex: number = -1;
					for (let i = 0; i < playerBeloteDeclarations.length; i++) {
						const dec = playerBeloteDeclarations[i];
						// 0 is QUEEN, 1 is KING
						// console.log("must be queen: ",CardRank[dec.declarationCards.cards[0].rank]);
						if (dec.declarationCards.cards[0].suit == card.suit) {
							if (dec.declarationCards.cards[0].rank == cardRank) {
								dec.declarationCards.played[0] = true;
							} else if (dec.declarationCards.cards[1].rank == cardRank) {
								dec.declarationCards.played[1] = true;
							}
							decIndex = i;
							break;
						}
					}
					// if found the declaration
					if (decIndex != -1) {
						const dec = playerBeloteDeclarations[decIndex];
						const player: Player = this.table.players.GetPlayerByIndex(lastPlayerIndex);
						let beloteCardsPlayed: number = 0;
						for (let i = 0; i < dec.declarationCards.played.length; i++) {
							if (dec.declarationCards.played[i] == true) {
								beloteCardsPlayed++;
							}
						}
						// console.log(beloteCardsPlayed)
						if (beloteCardsPlayed == 1) {
							this.table.GetStateMachine().AddDealBonusPoints(dec);
							player.Say(AppLocalization.GetSharedData().table.declarations.belote);
						} else if (beloteCardsPlayed == 2) {
							player.Say(AppLocalization.GetSharedData().table.declarations.rebelote);
						}
					}
				}
			}
		}

		//

		// // if got major declaration
		// const playerBeloteDeclarations: PlayerDeclaration[] = declarations.filter(d=>d.declarationType == DeclarationType.BELOTE_REBELOTE);
		// const hasMajorDeclarations = majorDeclarations.length > 0;
		// if (hasMajorDeclarations) {
		//     const majorDeclaration: PlayerDeclaration = majorDeclarations[0];
		//     // mark declaration cards shown (not needed for MAJOR declaration, where 'played' data is not used for state recovery)
		//     for (let i=0;i<majorDeclaration.declarationCards.played.length; i++) { majorDeclaration.declarationCards.played[i] = true; }

		//     // console.log(DeclarationType[majorDeclaration.declarationType]);

		//     // add declaration points as bonus points
		//     // this can be MAJOR declaration or BELOTE declaration
		//     this.AddDealBonusPoints(majorDeclaration);

		//     // player bubble with declaration type
		//     const playerSays:string = DeclarationType[majorDeclaration.declarationType].toString();
		//     this.table.players.GetPlayerByIndex(majorDeclaration.playerIndex).Say(playerSays);

		// // player bubble with declaration type
		// const playerSays:string = DeclarationType[majorDeclaration.declarationType].toString();
		// this.table.players.GetPlayerByIndex(majorDeclaration.playerIndex).Say(playerSays);

		onComplete();
	}

	/**
	 * Returns all cards that can be played in current trick by player index
	 * - trick might be incomplete
	 * - suggestedCards are result of further filtration/subset of playableCards
	 *   returns smaller array of cards (not all playable cards should be suggested,
	 *   only those further filtered - eg 'only lowest rank from 'all playable suit cards')
	 *
	 * @param playerIndex
	 */
	public ResolvePlayableAndSuggestedCards(playerIndex: number): void {
		// console.log("ResolvePlayableAndSuggestedCards", playerIndex);

		this.playableCards = [];
		this.suggestedCards = [];
		this.botPlayCard = null;
		this.hintText = '';

		// get ref
		const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
		const tableData: TableData = tableStateMachine.GetData();
		const trumpSuit: TrumpSuit = tableData.trumpSuit;
		const tableCards: Card[] = this.table.tableStackPlay.GetCards(); // no items = [] (empty array)
		const poolCards: Card[] = CardsManager.GetCards();
		const tableCardsCount: number = tableCards.length;
		const teammateIndex: number = tableStateMachine.GetFixedPlayerIndex(playerIndex + 2);
		// const hasTeammatePlayed: boolean = tableCardsCount > 1;
		// const leadPlayerIndex: number = tableData.trickDealerIndex;
		// const leadPlayerSide: PlayerSide = GetPlayerSide(leadPlayerIndex);
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		const playerSide: PlayerSide = GetPlayerSide(playerIndex);
		// copy so that highlight works (coz player.ReorderCards pops from the array and the reference bugs somehow also - eg this ref would keep old empty popped array as copy)
		const playerCards: Card[] = player.GetCards().slice();
		// const isLivePlayer: boolean = playerIndex == Players.PLAYER_INDEX;
		// console.log("leadSuit",CardSuit[leadSuit]);
		// const attackingSide: PlayerSide = GetPlayerSide(tableData.playerAttackerIndex);
		// Coinche || Contree table
		const isContraVariation: boolean = tableData.bids && tableData.bids.highestChoiceValueIndex != -1;
		const aiLevel: AILevel = tableStateMachine.GetData().options.aiLevel;

		const tableCardsBySuit: Card[][] = [];
		tableCards.forEach((card) => {
			if (tableCardsBySuit[card.suit] == undefined) {
				tableCardsBySuit[card.suit] = [];
			}
			tableCardsBySuit[card.suit].push(card);
		});
		
		const playerCardsBySuit: Card[][] = [];
		playerCards.forEach((card) => {
			if (playerCardsBySuit[card.suit] == undefined) {
				playerCardsBySuit[card.suit] = [];
			}
			playerCardsBySuit[card.suit].push(card);
		});
		// sort player cards by suit from highest to lowest rank (inside each suit)
		playerCardsBySuit.forEach(suitCards => {
			suitCards.sort((cardA, cardB)=>cardB.GetRank(TrumpMode.ALL_TRUMPS, trumpSuit) - cardA.GetRank(TrumpMode.ALL_TRUMPS, trumpSuit));
		});

		const poolCardsBySuit: Card[][] = [];
		poolCards.forEach((card) => {
			if (poolCardsBySuit[card.suit] == undefined) {
				poolCardsBySuit[card.suit] = [];
			}
			poolCardsBySuit[card.suit].push(card);
		});

		let trumpMode: TrumpMode;
		let trumpSuitAsCardSuit: CardSuit;
		switch (trumpSuit) {
			// game mode: ALL TRUMPS
			case TrumpSuit.ALLTRUMPS:
				const atPoints258: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
					OptionsItemKeys.atPoints258,
					this.table.variationId
				);
				trumpMode = atPoints258 && atPoints258.value ? TrumpMode.ALL_TRUMPS_258 : TrumpMode.ALL_TRUMPS;
				break;
			// game mode: NO TRUMPS
			case TrumpSuit.NOTRUMPS:
				const ntPoints130: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
					OptionsItemKeys.ntPoints130,
					this.table.variationId
				);
				trumpMode = ntPoints130 && ntPoints130.value ? TrumpMode.NO_TRUMPS_130 : TrumpMode.NO_TRUMPS;
				break;
			// game mode: TRUMP & OTHER
			case TrumpSuit.CLUBS:
			case TrumpSuit.DIAMONDS:
			case TrumpSuit.HEARTS:
			case TrumpSuit.SPADES:
				trumpMode = TrumpMode.TRUMP_OTHER;
				trumpSuitAsCardSuit = TrumpSuitToCardSuit(trumpSuit);
				break;
		}

		// this player is first to play in this trick
		if (tableCardsCount == 0) {
			switch (trumpSuit) {
				// game mode: ALL TRUMPS
				case TrumpSuit.ALLTRUMPS:
					this.ResolvePlayableAndSuggestedCards1stNTAT(playerIndex, trumpMode, playerCardsBySuit, poolCardsBySuit);
					return;

				// game mode: NO TRUMPS
				case TrumpSuit.NOTRUMPS:
					this.ResolvePlayableAndSuggestedCards1stNTAT(playerIndex, trumpMode, playerCardsBySuit, poolCardsBySuit);
					return;

				// game mode: TRUMP & OTHER
				case TrumpSuit.CLUBS:
				case TrumpSuit.DIAMONDS:
				case TrumpSuit.HEARTS:
				case TrumpSuit.SPADES:
					// got trump cards
					const playerTrumpCards: Card[] = playerCards.filter((card) => card.suit == trumpSuitAsCardSuit);
					// sort descending
					playerTrumpCards.sort((cardA, cardB) => cardB.GetRank(TrumpMode.ALL_TRUMPS) - cardA.GetRank(TrumpMode.ALL_TRUMPS));
					const playerOtherCards: Card[] = playerCards.filter((card) => card.suit != trumpSuitAsCardSuit);
					// lowest to highest rank (sort in place, return lowest)
					playerOtherCards.sort((cardA, cardB) => cardA.GetRank(TrumpMode.NO_TRUMPS) - cardB.GetRank(TrumpMode.NO_TRUMPS));
					if (playerTrumpCards.length) {
						// AI that uses tricks history
						// NOTE: player always get EXPERT hints
						//       bots play EXPERT only if EXPERT AI
						if (aiLevel === AILevel.EXPERT || Players.PLAYER_INDEX === playerIndex) {
							// check UNBEATABLE OTHER
							// for each suit
							for (let i = 0; i < 4; i++) {
								// check for unbeatable other:
								// run rank from top to bottom:
								// - if card in pool, continue.
								// - if on player, got unbeatable.
								// - if neither, someone else's got it - unbeatable not on player
								if (i != trumpSuitAsCardSuit) {
									const unbeatableOtherCards: Card[] = this.GetUnbeatableSuitCards(
										playerCardsBySuit[i] ?? [],
										poolCardsBySuit[i] ?? [],
										TrumpMode.NO_TRUMPS
									);
									// got unbeatable other(s)
									if (unbeatableOtherCards.length) {
										// suggest play unbeatable other(s)
										this.playableCards = playerCards;
										// suggest highest rank card (0 index; sorted prior)
										this.suggestedCards = [unbeatableOtherCards[0]];
										this.botPlayCard = unbeatableOtherCards[0];
										// "h252": "Keep your last trump card.",
										// "h250": "Keep your trump cards.",
										this.hintText = AppLocalization.GetHintTextById(playerTrumpCards.length == 1 ? 'h252' : 'h250');
										// "h157": "Play an unbeatable card.",
										this.hintText += " " + AppLocalization.GetHintTextById('h157');
										return;
									}
								}
							}

							// got trump card(s)
							// got no other card(s)
							if (!playerOtherCards.length) {
								// got unbeatable trump? (HISTORY)
								// NOTE: unbeatableTrumpCards are already sorted descending (since playerTrumpCards are)
								const unbeatableTrumpCards: Card[] = this.GetUnbeatableSuitCards(
									playerTrumpCards,
									poolCardsBySuit[trumpSuitAsCardSuit] ?? [],
									TrumpMode.ALL_TRUMPS
								);
								// sort descending
								unbeatableTrumpCards.sort(
									(cardA: Card, cardB: Card) => cardB.GetRank(TrumpMode.ALL_TRUMPS) - cardA.GetRank(TrumpMode.ALL_TRUMPS)
								);

								/*
								"h400": "You bid the contract, play trump.", <--- we go first
								"h158": "Play an unbeatable trump card.", <--- if got one
								"h152": "Play your weakest trump card.", <--- if someone else got highest trump
								"h401": "Your partner bid the contract, play trump.",
								*/
								if (isContraVariation) {
									// this player's contract
									const isOurContract =
										playerIndex === tableData.playerAttackerIndex || teammateIndex === tableData.playerAttackerIndex;
									if (isOurContract) {
										if (playerIndex === tableData.playerAttackerIndex) {
											// "h400": "You bid the contract, play trump."
											this.hintText = AppLocalization.GetHintTextById('h400');
										} else if (teammateIndex === tableData.playerAttackerIndex) {
											// "h401": "Your partner bid the contract, play trump.",
											this.hintText = AppLocalization.GetHintTextById('h401');
										}
									}
								}
								// got unbeatable trump card(s)
								if (unbeatableTrumpCards.length) {
									// "h158": "Play an unbeatable trump card."
									this.hintText += " " + AppLocalization.GetHintTextById('h158');
									this.playableCards = playerCards;
									this.suggestedCards = unbeatableTrumpCards;
									// (if bot) play highest
									this.botPlayCard = unbeatableTrumpCards[0];
									return;
								}
								// got trump cards, all beatable
								else {
									// "h152": "Play your weakest trump card."
									this.hintText += " " + AppLocalization.GetHintTextById('h152');
									this.playableCards = playerCards;
									this.suggestedCards = playerTrumpCards;
									// (if bot) play lowest
									this.botPlayCard = playerTrumpCards[playerTrumpCards.length - 1];
									return;
								}
							}
						}

						// > got trump card(s)
						// got other card(s)
						// dont have unbeatable other card || this is EasyAI
						if (playerOtherCards.length) {
							const lowestOther: Card = playerOtherCards[0];
							this.playableCards = playerCards;
							// suggest lowest non trump
							this.suggestedCards = [lowestOther];
							// this.botPlayCard = lowestOther; // keep random or deterministic based on AI @see TaskResolvePlayCard

							// "h250": "Keep your trump cards.",
							// "h252": "Keep your last trump card.",
							this.hintText = AppLocalization.GetHintTextById(playerTrumpCards.length > 1 ? 'h250' : 'h252');
							// "h155": "Play a weak card.",
							if (playerOtherCards.length > 1) {
								this.hintText += " " + AppLocalization.GetHintTextById('h155');
							}
							return;
						}
					}
					break;
			}
		}
		// this player is 2-4th to play in this trick (1+ cards on table)
		else {
			const leadCard: Card | null = this.table.tableStackPlay.GetFirst();
			const leadSuit: CardSuit = leadCard == null ? null : leadCard.suit;
			const playerLeadSuitCards: Card[] = playerCards.filter((card) => card.suit == leadSuit);
			this.GetTrickWinner(); // update trick winner data
			const trickWinnerSide: PlayerSide = GetPlayerSide(this.trickWinnerData.playerIndex);
			
			switch (trumpSuit) {
				// game mode: ALL TRUMPS
				case TrumpSuit.ALLTRUMPS:
					this.ResolvePlayableAndSuggestedCardsNthNTAT(playerIndex, trumpMode, playerLeadSuitCards);
					return;

				// game mode: NO TRUMPS
				case TrumpSuit.NOTRUMPS:
					this.ResolvePlayableAndSuggestedCardsNthNTAT(playerIndex, trumpMode, playerLeadSuitCards);
					return;

				// game mode: TRUMP & OTHER
				case TrumpSuit.CLUBS:
				case TrumpSuit.DIAMONDS:
				case TrumpSuit.HEARTS:
				case TrumpSuit.SPADES:
					const trumpSuitAsCardSuit: CardSuit = TrumpSuitToCardSuit(trumpSuit);
					// If leading suit is trump
					if (leadSuit == trumpSuitAsCardSuit) {
						// if player can follow trump lead suit
						if (playerLeadSuitCards.length) {
							// if teammate is winning
							// - player can play any trump (even lower trump than the currently winning one)
							if (playerSide == trickWinnerSide) {
								const lowestCard = this.GetLowestRankCard(playerLeadSuitCards, trumpSuit, trumpMode);
								this.playableCards = playerLeadSuitCards;
								this.suggestedCards = [lowestCard];
								this.botPlayCard = lowestCard;

								// "h301": "Your team is winning the trick.",
								// "h170": "Play any trump card.",
								this.hintText = AppLocalization.GetHintTextById('h301');
								this.hintText += " " + AppLocalization.GetHintTextById('h170');
								return;
							}
							// teammate didnt play or your team is losing
							else {
								const highestLeadSuitTableCard: Card = this.GetHighestRankCard(tableCards, trumpSuit, trumpMode, leadSuit);
								const highestLeadSuitTableCardRank: number = highestLeadSuitTableCard.GetRank(trumpMode, trumpSuit);
								const higherPlayerLeadSuitCards: Card[] = playerLeadSuitCards.filter((card) => {
									return card.GetRank(trumpMode, trumpSuit) > highestLeadSuitTableCardRank;
								});

								// if player has higher trump than so far played:
								// - player must play higher trump
								if (higherPlayerLeadSuitCards.length) {
									const lowestHigherPlayerLeadSuitCard = this.GetLowestRankCard(
										higherPlayerLeadSuitCards,
										trumpSuit,
										trumpMode
									);
									this.playableCards = higherPlayerLeadSuitCards;
									this.suggestedCards = [lowestHigherPlayerLeadSuitCard];
									this.botPlayCard = lowestHigherPlayerLeadSuitCard;

									if (this.playableCards.length > 1) {
										// "h109": "Your must overtrump with one of your higher rank trump cards."
										this.hintText = AppLocalization.GetHintTextById('h109');
									} else {
										// "h110": "Your must overtrump with your only higher rank trump card.",
										this.hintText = AppLocalization.GetHintTextById('h110');
									}
									return;
								}
								// if player does not have higher trump than so far played:
								// - player can play any trump card (hint: lowest rank)
								// NOTE: there is at least 1
								else {
									const lowestCard = this.GetLowestRankCard(playerLeadSuitCards, trumpSuit, trumpMode);
									this.playableCards = playerLeadSuitCards;
									this.suggestedCards = [lowestCard];
									this.botPlayCard = lowestCard;
									// console.log(lowestCard.log(), playerLeadSuitCards.map(c=>c.log()));
									
									if (this.playableCards.length > 1) {
										// "h152": "Play your weakest trump card.",
										this.hintText = AppLocalization.GetHintTextById('h152');
									} else {
										// "h350": "You have only one playable card.",
										this.hintText = AppLocalization.GetHintTextById('h350');
									}
									return;
								}
							}
						}
						// if player can not follow lead suit (trump suit):
						// - player can play any card
						// TODO: suggestedCards, better hints
						//       CONSIDER: based on 'cards played', 'hasTeammatePlayed'
						else {
							const lowestCard = this.GetLowestRankCard(playerCards, trumpSuit, trumpMode);
							this.playableCards = playerCards;
							this.suggestedCards = [lowestCard];
							this.botPlayCard = lowestCard;

							// "h500": "You don't have a trump card.",
							// "h168": "Play any card.",
							this.hintText = AppLocalization.GetHintTextById('h500');
							this.hintText += " " + AppLocalization.GetHintTextById('h168');
							return;
						}
					}
					// If lead suit is other
					else {
						// > player is 2-4th to play in this trick
						// > game mode: TRUMP & OTHER
						// > lead suit is other
						// if player can follow lead suit
						// - player can play any lead suit card (even if teammate is loosing or enemy trumped)
						if (playerLeadSuitCards.length) {
							// > player got lead suit card(s)
							// we are winning
							if (playerSide == trickWinnerSide) {
								this.playableCards = playerLeadSuitCards;
								if (playerLeadSuitCards.length > 1) {
									// player is last to play
									if (tableCardsCount == 3) {
										// play high value card to butter the won trick
										const highestCard = this.GetHighestRankCard(playerLeadSuitCards, trumpSuit, trumpMode);
										this.suggestedCards = [highestCard];
										this.botPlayCard = highestCard;

										// "h301": "Your team is winning the trick.",
										// "h450": "Save points.",
										this.hintText = AppLocalization.GetHintTextById('h301');
										this.hintText += " " + AppLocalization.GetHintTextById('h450');
										return;
									}
									else {
										// player is not last to play (someone after could take trick)
										const lowestCard = this.GetLowestRankCard(playerLeadSuitCards, trumpSuit, trumpMode);
										this.suggestedCards = [lowestCard];
										this.botPlayCard = lowestCard;
										
										// "h301": "Your team is winning the trick.",
										// "h151": "Play your weakest card.",
										this.hintText = AppLocalization.GetHintTextById('h151');
										return;
									}
										
								} 
								else {
									// console.log(this.playableCards.map(card=>card.log()));
									// console.log(this.suggestedCards.map(card=>card.log()));
									this.suggestedCards = playerLeadSuitCards;
									// "h350": "You have only one playable card.",
									this.hintText = AppLocalization.GetHintTextById('h350');
									return;
								}
							}
							// they are winning
							else {
								// they trumped
								const trickWinnerCard = tableCards[this.trickWinnerData.trickCardIndex];
								if (trickWinnerCard.suit == trumpSuitAsCardSuit) {
									// if player has trump cards
									const playerTrumpCards: Card[] = playerCards.filter(card => card.suit == trumpSuitAsCardSuit);
									// console.log(playerTrumpCards.map(c=>c.log()));
									if (playerTrumpCards.length) {
										const trickWinnerCardRank = trickWinnerCard.GetRank(trumpMode, trumpSuit);
										const higherPlayerTrumpCards = this.GetHigherRankCards(playerTrumpCards, trumpSuit, trumpMode, trickWinnerCardRank);
										// console.log(higherPlayerTrumpCards.map(card=>card.log()));
										// player has higher trump card than what enemy played
										if (higherPlayerTrumpCards.length) {
											// player plays lowest of higher trump cards than what enemy played
											const playCard = this.GetLowestRankCard(higherPlayerTrumpCards, trumpSuit, trumpMode);
											this.playableCards = higherPlayerTrumpCards; // any is playable
											this.suggestedCards = [playCard];
											this.botPlayCard = playCard;
											
											// console.log("player does have higher trump card(s) than what enemy played; player plays lower of higher trump card(s); suggest: lower from higher ranking trump: "+playCard.log());

											// "h353": "The opposing team trumped.",
                						// "h355": "You can beat the winning card.",
                						// "h165": "Play your stronger trump card.",
											this.hintText = AppLocalization.GetHintTextById('h353');
											this.hintText += " " + AppLocalization.GetHintTextById('h355');
											this.hintText += " " + AppLocalization.GetHintTextById('h165');
											return;
										}
										// player does not have higher trump card than what enemy played
										else {
											const isPissTrump = OptionsDataManager.IsPissTrump(this.table.variationId);
											// pissTrump active
											if (isPissTrump) {
												// player plays trump card; suggest: lowest ranking trump
												const playCard = this.GetLowestRankCard(playerTrumpCards, trumpSuit, trumpMode);
												this.playableCards = playerTrumpCards; // any is playable
												this.suggestedCards = [playCard];
												this.botPlayCard = playCard;

												// console.log("player does not have higher trump card than what enemy played; pissTrump is active; player plays trump card; suggest: lowest ranking trump: "+playCard.log());

												// "h353": "The opposing team trumped.",
												// "h352": "You can't beat the winning card.",
                							// "h152": "Play your weakest trump card.",
												this.hintText = AppLocalization.GetHintTextById('h353');
												this.hintText += " " + AppLocalization.GetHintTextById('h352');
												this.hintText += " " + AppLocalization.GetHintTextById('h152');
												return;
											}
											// pissTrump not active
											else {
												// player plays any card; suggest: lowest ranking any
												const playCard = this.GetLowestRankCard(playerCards, trumpSuit, trumpMode, GetCardSuitsExcept(TrumpSuitToCardSuit(trumpSuit)));
												this.playableCards = playerCards; // any is playable
												this.suggestedCards = [playCard];
												this.botPlayCard = playCard;

												// console.log("player does not have higher trump card than what enemy played; pissTrump not active; player plays any card; suggest: lowest ranking any: "+playCard.log());
												
												// "h353": "The opposing team trumped.",
												// "h352": "You can't beat the winning card.",
												// "h151": "Play your weakest card.",
												this.hintText = AppLocalization.GetHintTextById('h353');
												this.hintText += " " + AppLocalization.GetHintTextById('h352');
												this.hintText += " " + AppLocalization.GetHintTextById('h151');
												return;
											}
										}
									}
									// player does not have trump card
									else {
										// player plays any card; suggest: lowest ranking any
										const playCard = this.GetLowestRankCard(playerCards, trumpSuit, trumpMode);
										this.playableCards = playerCards; // any is playable
										this.suggestedCards = [playCard];
										this.botPlayCard = playCard;
										
										// console.log("player does not have a trump card; player plays any card; suggest: lowest ranking any: "+playCard.log());
										
										// "h353": "The opposing team trumped.",
										// "h356": "You don't have a trump card.",
										// "h151": "Play your weakest card.",
										this.hintText = AppLocalization.GetHintTextById('h353');
										this.hintText += " " + AppLocalization.GetHintTextById('h356');
										this.hintText += " " + AppLocalization.GetHintTextById('h151');
										return;
									}
								}
								// they did not trump
								// - they are winning with Other (which also is Lead and a winning suit)
								else {
									const highestLeadSuitTableCard: Card = this.GetHighestRankCard(tableCards, trumpSuit, trumpMode, leadSuit);
									const highestLeadSuitTableCardRank: number = highestLeadSuitTableCard.GetRank(trumpMode, trumpSuit);
									const higherPlayerLeadSuitCards: Card[] = playerLeadSuitCards.filter((card) => {
										return card.GetRank(trumpMode, trumpSuit) > highestLeadSuitTableCardRank;
									});

									// player has higher lead suit card than so far played:
									// - player plays lowest from higher lead suit cards
									if (higherPlayerLeadSuitCards.length) {
										const lowestHigherPlayerLeadSuitCard = this.GetLowestRankCard(
											higherPlayerLeadSuitCards,
											trumpSuit,
											trumpMode
										);
										this.playableCards = higherPlayerLeadSuitCards;
										this.suggestedCards = [lowestHigherPlayerLeadSuitCard];
										this.botPlayCard = lowestHigherPlayerLeadSuitCard;
										
										// console.log("player has higher lead suit card than so far played; player plays lowest from higher lead suit cards: "+lowestHigherPlayerLeadSuitCard.log());

										// "h156": "Play a stronger card.",
										// "h167": "Play the stronger lead suit card.",
										this.hintText = AppLocalization.GetHintTextById('h167');
										return;
									}
									// player does not have higher lead suit card than so far played
									// - player plays any lower lead suit card; suggest: lowest
									else {
										const playCard = this.GetLowestRankCard(playerLeadSuitCards, trumpSuit, trumpMode);
										this.playableCards = playerLeadSuitCards;
										this.suggestedCards = [playCard];
										this.botPlayCard = playCard;
										
										// console.log("player does not have higher lead suit card than so far played; player plays lowers lead suit card: "+playCard.log());

										if (this.playableCards.length > 1) {
											// "h155": "Play a weak card.",
											this.hintText = AppLocalization.GetHintTextById('h155');
										} else {
											// "h166": "Play your only lead suit card.",
											this.hintText = AppLocalization.GetHintTextById('h166');
										}
										return;
									}
								}
							}
						}
						// > lead is not trump
						// if player can not follow lead suit
						else {
							const playerTrumpCards: Card[] = playerCards.filter((card) => card.suit == trumpSuitAsCardSuit);
							// > player is 2-4th to play in this trick
							// > game mode: TRUMP & OTHER
							// > leading suit not trump
							// > player can not follow lead suit
							// if they are winning
							if (playerSide != trickWinnerSide) {
								// console.log(tableCards.map(card=>card.log()));
								// console.log(playerTrumpCards.map(card=>card.log()));
								// if player has trump
								if (playerTrumpCards.length) {
									// console.log(playerTrumpCards.map(card=>card.log()+"("+card.GetRank(trumpSuit, trumpMode)+")"));
									const highestTableTrumpCard: Card | null = this.GetHighestRankCard(tableCards, trumpSuit, trumpMode, trumpSuitAsCardSuit);
									// if enemy played trump card
									if (highestTableTrumpCard) {
										const highestTableTrumpCardRank: number = highestTableTrumpCard.GetRank(trumpMode, trumpSuit);
										const higherPlayerTrumpCards: Card[] = this.GetHigherRankCards(playerTrumpCards, trumpSuit, trumpMode, highestTableTrumpCardRank);

										// if higher trump has been played [by enemy] than what player has:
										if (!higherPlayerTrumpCards.length) {
											const isPissTrump = OptionsDataManager.IsPissTrump(this.table.variationId);
											// If pissTrump=TRUE:
											// - player must play his lower trump (underTrump=pissTrump)
											if (isPissTrump) {
												const lowerPlayerTrumpCards = this.GetLowerRankCards(playerTrumpCards, trumpSuit, trumpMode, highestTableTrumpCardRank);

												this.playableCards = lowerPlayerTrumpCards;
												// multiple lower trump cards
												if (lowerPlayerTrumpCards.length > 1) {
													const lowestCard = this.GetLowestRankCard(lowerPlayerTrumpCards, trumpSuit, trumpMode);
													this.suggestedCards = [lowestCard];
													this.botPlayCard = lowestCard;

													// "h358": "You don't have a trump to overtrump with.",
													// "h108": "You must undertrump.",
													// "h152": "Play your weakest trump card.",
													this.hintText = AppLocalization.GetHintTextById('h358');
													this.hintText += " " + AppLocalization.GetHintTextById('h108');
													this.hintText += " " + AppLocalization.GetHintTextById('h152');
													return;
												}
												// single lower trump card
												else {
													this.suggestedCards = lowerPlayerTrumpCards;

													// "h358": "You don't have a trump to overtrump with.",
													// "h108": "You must undertrump.",
													// "h154": "Play your last trump card.",
													this.hintText = AppLocalization.GetHintTextById('h358');
													this.hintText += " " + AppLocalization.GetHintTextById('h108');
													this.hintText += " " + AppLocalization.GetHintTextById('h154');
													return;
												}
											}
											// If pissTrump=FALSE:
											// - player can play any card.
											else {
												this.playableCards = playerCards;
												// got multiple playing cards
												if (playerCards.length > 1) {
													// play lowest
													const lowestCard = this.GetLowestRankCard(playerCards, trumpSuit, trumpMode);
													this.suggestedCards = [lowestCard];
													this.botPlayCard = lowestCard;

													// "h358": "You don't have a trump to overtrump with.",
													// "h168": "Play any card.",
													this.hintText = AppLocalization.GetHintTextById('h358');
													this.hintText += " " + AppLocalization.GetHintTextById('h168');
												}
												// last playing card
												else {
													this.suggestedCards = playerCards;

													// "h350": "You have only one playable card.",
													this.hintText = AppLocalization.GetHintTextById('h350');
												}
												return;
											}
										}
										// if lower trump has been played [by enemy] than what player has:
										// - player must play his higher trump (overTrump=default).
										else {
											this.playableCards = higherPlayerTrumpCards;
											// multiple higher trump cards
											if (higherPlayerTrumpCards.length > 1) {
												// NOTE: suggesting weakest from higher trump cards
												const lowestCard = this.GetLowestRankCard(higherPlayerTrumpCards, trumpSuit, trumpMode);
												this.suggestedCards = [lowestCard];
												this.botPlayCard = lowestCard;

												// "h165": "Play your stronger trump card.",
												this.hintText = AppLocalization.GetHintTextById('h165');
												return;
											}
											// single higher trump card
											else {
												this.suggestedCards = higherPlayerTrumpCards;

												// "h350": "You have only one playable card.",
												this.hintText = AppLocalization.GetHintTextById('h350');
												return;
											}
										}
									}
									// if enemy has not played trump:
									// - player has to play trump to take the trick
									else {
										this.playableCards = playerTrumpCards;
										// if lead suit of this trick was previously played as lead suit of another trick
										// TODO/assumption: fork base on 'was suit played as lead before/history'
										if (this.WasLeadTrickSuitPlayed(leadSuit)) {
											if (this.playableCards.length > 1) {
												const highestCard = this.GetHighestRankCard(playerTrumpCards, trumpSuit, trumpMode);
												this.suggestedCards = [highestCard];
												this.botPlayCard = highestCard;

												// "h153": "Play your strongest trump card.",
												this.hintText = AppLocalization.GetHintTextById('h153');
												return;
											} else {
												this.suggestedCards = playerTrumpCards;

												// "h350": "You have only one playable card.",
												this.hintText = AppLocalization.GetHintTextById('h350');
												return;
											}
										}
										// lead suit of this trick was previously NOT played as lead suit of another trick
										else {
											if (this.playableCards.length > 1) {
												const lowestCard = this.GetLowestRankCard(playerTrumpCards, trumpSuit, trumpMode);
												this.suggestedCards = [lowestCard];
												this.botPlayCard = lowestCard;

												// "h164": "Play your weakest trump card.",
												this.hintText = AppLocalization.GetHintTextById('h164');
												return;
											} else {
												this.suggestedCards = playerTrumpCards;

												// "h350": "You have only one playable card.",
												this.hintText = AppLocalization.GetHintTextById('h350');
												return;
											}
										}
									}
								}
								// if player does not have trump:
								// - player can play any card.
								// TODO: suggestedCards, better hints
								//       CONSIDER: based on 'cards played', 'hasTeammatePlayed'
								else {
									this.playableCards = playerCards;
									if (playerCards.length > 1) {
										const lowestCard = this.GetLowestRankCard(playerCards, trumpSuit, trumpMode);
										this.suggestedCards = [lowestCard];
										this.botPlayCard = lowestCard;

										// "h500": "You don't have a trump card.",
										// "h168": "Play any card.",
										this.hintText = AppLocalization.GetHintTextById('h500');
										this.hintText += " " + AppLocalization.GetHintTextById('h168');
										return;
									} else {
										this.suggestedCards = playerCards;

										// "h350": "You have only one playable card.",
										this.hintText = AppLocalization.GetHintTextById('h350');
										return;
									}
								}
							}
							// > player is 2-4th to play in this trick
							// > game mode: TRUMP & OTHER
							// > If leading suit is non-trump
							// > if player can not follow non-trump lead suit
							// If teammate is winning trick
							// - player can play any card (hint: play high card / save points)
							else {
								// if player has trump
								if (playerTrumpCards.length) {
									const forceTrumpPlay: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
										OptionsItemKeys.forceTrumpPlay,
										this.table.variationId
									);
									// if forceTrumpPlay=TRUE:
									// - player must play trump (even if higher trump was played or his team is winning).
									if (forceTrumpPlay && forceTrumpPlay.value) {
										this.playableCards = playerTrumpCards;
										// multiple lower trump cards
										if (playerTrumpCards.length > 1) {
											const lowestCard = this.GetLowestRankCard(playerTrumpCards, trumpSuit, trumpMode);
											this.suggestedCards = [lowestCard];
											this.botPlayCard = lowestCard;

											// Your team is winning the trick. You have to play a trump card. (NOTE: because forceTrumpPlay is ON)(NOTE: suggest weakest trump card)
											// "h300": "Your team is winning the trick, but you are forced to trump.",
											// "h152": "Play your weakest trump card.",
											this.hintText = AppLocalization.GetHintTextById('h300');
											this.hintText += " " + AppLocalization.GetHintTextById('h152');
										} 
										// single lower trump card
										else {
											this.suggestedCards = playerTrumpCards;

											// Your team is winning the trick. You have to play your last trump card. (NOTE: because forceTrumpPlay is ON
											// "h300": "Your team is winning the trick, but you are forced to trump.",
											// "h154": "Play your last trump card.",
											this.hintText = AppLocalization.GetHintTextById('h300');
											this.hintText += " " + AppLocalization.GetHintTextById('h154');
										}
										return;
									}
									// If forceTrumpPlay=FALSE:
									// - player can play any card.
									else {
										this.playableCards = playerCards;
										this.suggestedCards = playerCards;
										if (playerCards.length > 1) {
											// "h301": "Your team is winning the trick.",
											// "h168": "Play any card.",
											this.hintText = AppLocalization.GetHintTextById('h301');
											this.hintText += " " + AppLocalization.GetHintTextById('h168');
										} else {
											// "h350": "You have only one playable card.",
											this.hintText = AppLocalization.GetHintTextById('h350');
										}
										return;
									}
								}
								// if player does not have trump:
								// - player can play any card.
								else {
									this.playableCards = playerCards;
									this.suggestedCards = playerCards;
									if (playerCards.length > 1) {
										// "h500": "You don't have a trump card.",
										// "h168": "Play any card.",
										this.hintText = AppLocalization.GetHintTextById('h500');
										this.hintText += " " + AppLocalization.GetHintTextById('h168');
									} else {
										// "h350": "You have only one playable card.",
										this.hintText = AppLocalization.GetHintTextById('h350');
									}
									return;
								}
							}
						}
					}
					break;
			}
		}

		// if everything eles fails
		this.playableCards = playerCards;
		this.suggestedCards = playerCards;

		// "h150": "Play one of these cards.",
		this.hintText = AppLocalization.GetHintTextById('h150');

		// console.log(this.suggestedCards.map(card=>card.log()));
	}
	
	private ResolvePlayableAndSuggestedCards1stNTAT(
		playerIndex: number,
		trumpMode: TrumpMode,
		playerCardsBySuit: Card[][],
		poolCardsBySuit: Card[][],
	): void {
		const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
		const tableData: TableData = tableStateMachine.GetData();
		// const tableCards: Card[] = this.table.tableStackPlay.GetCards();
		const trumpSuit = tableData.trumpSuit;
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		// const playerSide: PlayerSide = GetPlayerSide(playerIndex);
		const playerCards: Card[] = player.GetCards().slice();
		const leadCard: Card | null = this.table.tableStackPlay.GetFirst();
		const leadSuit: CardSuit = leadCard == null ? null : leadCard.suit;
		const aiLevel: AILevel = tableStateMachine.GetData().options.aiLevel;
		// if player (EXPERT hints) or
		// if EXPERT ai (bot plays; uses tricks history)
		if (aiLevel === AILevel.EXPERT || Players.PLAYER_INDEX === playerIndex) {
			// play unbeatable if on hand
			for (let i=0; i<4; i++) {
				let playerSuitCards: Card[] = playerCardsBySuit[i] ?? [];
				const poolSuitCards: Card[] = poolCardsBySuit[i] ?? [];
				const unbeatableSuitCards: Card[] = this.GetUnbeatableSuitCards(playerSuitCards, poolSuitCards, trumpMode);
				// console.log(CardSuit[i], unbeatableSuitCards.map(c=>c.log()));

				// if found unbeatable suit card
				if (unbeatableSuitCards.length) {
					// play it
					this.playableCards = playerCards;
					this.suggestedCards = unbeatableSuitCards;
					this.botPlayCard = unbeatableSuitCards[0];
					// "h157": "Play an unbeatable card.",
					this.hintText = AppLocalization.GetHintTextById('h157');
					return;
				}
			}

			// > player is first to play
			// > Expert mode or Expert hints
			// > no unbeatable on hand
			// mode is AT
			if (trumpSuit == TrumpSuit.ALLTRUMPS) {
				// player should not play a 9 without a J in hand when he's 1st and J was not played yet
				// - this needs to be run for each suit, since player can play any card/suit
				// - we will create new, filtered collection of Cards, where this applies.
				let filteredCards: Card[] = [];
				for (let i=0; i<4; i++) {
					let playerSuitCards: Card[] = playerCardsBySuit[i] ?? [];
					// player should not play a 9 without a J in hand when he's 1st and J was not played yet
					if (this.IsCardInCollection(playerSuitCards, i, CardRank.NINE) &&
						!this.IsCardInCollection(playerSuitCards, i, CardRank.JACK) &&
						!this.WasCardPlayed(i, CardRank.JACK)
					) {
						filteredCards = filteredCards.concat(playerSuitCards.filter(c=>(!(c.rank==CardRank.NINE)&&(c.suit==i))));
					}
					else {
						filteredCards = filteredCards.concat(playerSuitCards);
					}
				}
				// console.log(filteredCards.map(c=>c.log()));
				
				this.playableCards = playerCards;
				this.suggestedCards = filteredCards.length ? filteredCards : playerCards;

				// "h150": "Play one of these cards.",
				this.hintText = AppLocalization.GetHintTextById('h150');
				return;
			}
			else
			// mode is AT
			if (trumpSuit == TrumpSuit.NOTRUMPS) {
				// player should not play a 10 without a A in hand when he's 1st and A was not played yet
				// - this needs to be run for each suit, since player can play any card/suit
				// - we will create new, filtered collection of Cards, where this applies.
				let filteredCards: Card[] = [];
				for (let i=0; i<4; i++) {
					let playerSuitCards: Card[] = playerCardsBySuit[i] ?? [];
					// player should not play a 9 without a J in hand when he's 1st and J was not played yet
					if (this.IsCardInCollection(playerSuitCards, i, CardRank.TEN) &&
						!this.IsCardInCollection(playerSuitCards, i, CardRank.ACE) &&
						!this.WasCardPlayed(i, CardRank.ACE)
					) {
						filteredCards = filteredCards.concat(playerSuitCards.filter(c=>(!(c.rank==CardRank.TEN)&&(c.suit==i))));
					}
					else {
						filteredCards = filteredCards.concat(playerSuitCards);
					}
				}
				// console.log(filteredCards.map(c=>c.log()));
				
				this.playableCards = playerCards;
				this.suggestedCards = filteredCards.length ? filteredCards : playerCards;

				// "h150": "Play one of these cards.",
				this.hintText = AppLocalization.GetHintTextById('h150');
				return;
			}
		}

		// default
		this.playableCards = playerCards;
		this.suggestedCards = playerCards;
		// this.botPlayCard = this.playableCards[0]; // this is default
		// "h150": "Play one of these cards.",
		this.hintText = AppLocalization.GetHintTextById('h150');
		return;
	}

	/**
	 * Player 2nd-4th in trick, NT|AT; resolve playable/suggested cards/botCard
	 * @param playerIndex 
	 * @param trumpMode 
	 * @param playerLeadSuitCards 
	 * @returns 
	 */
	private ResolvePlayableAndSuggestedCardsNthNTAT(
		playerIndex: number,
		trumpMode: TrumpMode,
		playerLeadSuitCards: Card[]
	): void {
		const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
		const tableData: TableData = tableStateMachine.GetData();
		const tableCards: Card[] = this.table.tableStackPlay.GetCards();
		const tableCardsCount: number = tableCards.length;
		// const hasTeammatePlayed: boolean = tableCardsCount > 1;
		const trumpSuit = tableData.trumpSuit;
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		const playerSide: PlayerSide = GetPlayerSide(playerIndex);
		const playerCards: Card[] = player.GetCards().slice();
		const leadCard: Card | null = this.table.tableStackPlay.GetFirst();
		const leadSuit: CardSuit = leadCard == null ? null : leadCard.suit;
		const trickWinnerSide: PlayerSide = GetPlayerSide(this.trickWinnerData.playerIndex);
		const aiLevel = this.table.GetTableAI().GetAILevel();
		
		// if player can follow lead suit:
		if (playerLeadSuitCards.length) {
			// if team is winning (hasTeammatePlayed=TRUE):
			// - player can play any card (even lower ranking card than the currently winning one)
			if (playerSide == trickWinnerSide) {
				// player is last to play
				if (tableCardsCount == 3) {
					// mode is AT
					if (trumpSuit == TrumpSuit.ALLTRUMPS) {
						if (aiLevel === AILevel.EXPERT || Players.PLAYER_INDEX === playerIndex) {
							// > player is 4th to play
							// > we are winning
							// mode is AT
							// player has 9 and not J and J was not played yet
							if (this.IsCardInCollection(playerCards, leadSuit, CardRank.NINE) &&
								!this.IsCardInCollection(playerCards, leadSuit, CardRank.JACK) &&
								!this.WasCardPlayed(leadSuit, CardRank.JACK)
							) {
								// play 9 to butter the won trick
								const card = this.GetCardFromCollection(playerCards, leadSuit, CardRank.NINE);
								this.playableCards = playerLeadSuitCards;
								this.suggestedCards = [card];
								this.botPlayCard = card;

                			// "h351": "You are the last to play.",
								// "h301": "Your team is winning the trick.",
                			// "h162": "Play a card of a suit of which you have the second strongest card.",
								// "h450": "Save points.",
								this.hintText = AppLocalization.GetHintTextById('h351');
								this.hintText += " " + AppLocalization.GetHintTextById('h301');
								this.hintText += " " + AppLocalization.GetHintTextById('h450');
								return;
							}
						}
					}
					else
					// mode is AT
					if (trumpSuit == TrumpSuit.NOTRUMPS) {
						if (aiLevel === AILevel.EXPERT || Players.PLAYER_INDEX === playerIndex) {
							// > player is 4th to play
							// > we are winning
							// mode is NT
							// player has 10 and not A and A was not played yet
							if (this.IsCardInCollection(playerCards, leadSuit, CardRank.TEN) &&
								!this.IsCardInCollection(playerCards, leadSuit, CardRank.ACE) &&
								!this.WasCardPlayed(leadSuit, CardRank.ACE)
							) {
								// play 10 to butter the won trick
								const card = this.GetCardFromCollection(playerCards, leadSuit, CardRank.TEN);
								this.playableCards = playerLeadSuitCards;
								this.suggestedCards = [card];
								this.botPlayCard = card;

                			// "h351": "You are the last to play.",
								// "h301": "Your team is winning the trick.",
                			// "h162": "Play a card of a suit of which you have the second strongest card.",
								// "h450": "Save points.",
								this.hintText = AppLocalization.GetHintTextById('h351');
								this.hintText += " " + AppLocalization.GetHintTextById('h301');
								this.hintText += " " + AppLocalization.GetHintTextById('h450');
								return;
							}
						}
					}
				}
				
				// else (if any of the above subcases didnt trigger), play any card
				this.playableCards = playerLeadSuitCards;
				this.suggestedCards = playerLeadSuitCards;
				this.botPlayCard = playerLeadSuitCards[0];
				if (this.playableCards.length > 1) {
					// "h301": "Your team is winning the trick.",
					// "h169": "Play any lead suit card.",
					this.hintText = AppLocalization.GetHintTextById('h301');
					this.hintText += " " + AppLocalization.GetHintTextById('h169');
				} else {
					// "h301": "Your team is winning the trick.",
					// "h166": "Play your only lead suit card.",
					this.hintText = AppLocalization.GetHintTextById('h301');
					this.hintText += " " + AppLocalization.GetHintTextById('h166');
				}
				return;
			}
			// if team is losing (hasTeammatePlayed=TRUE and losing, or hasTeammatePlayed=FALSE):
			else {
				const highestLeadSuitTableCard: Card = this.GetHighestRankCard(tableCards, trumpSuit, trumpMode, leadSuit);
				const highestLeadSuitTableCardRank: number = highestLeadSuitTableCard.GetRank(trumpMode, trumpSuit);
				const higherPlayerLeadSuitCards: Card[] = playerLeadSuitCards.filter(card => {
					return card.GetRank(trumpMode, trumpSuit) > highestLeadSuitTableCardRank;
				});

				// player has higher lead suit card than so far played:
				// - player plays lowest from higher lead suit cards
				if (higherPlayerLeadSuitCards.length) {
					const lowestHigherPlayerLeadSuitCard = this.GetLowestRankCard(higherPlayerLeadSuitCards, trumpSuit, trumpMode);
					this.playableCards = higherPlayerLeadSuitCards;
					this.suggestedCards = [lowestHigherPlayerLeadSuitCard];
					this.botPlayCard = lowestHigherPlayerLeadSuitCard;
					
					// console.log("player has higher lead suit card than so far played; player plays lowest from higher lead suit cards: "+lowestHigherPlayerLeadSuitCard.log());

					if (this.playableCards.length > 1) {
						this.suggestedCards = [this.GetHighestRankCard(higherPlayerLeadSuitCards, trumpSuit, trumpMode)];

						// "h355": "You can beat the winning card.",
						// "h167": "Play the stronger lead suit card.",
						this.hintText = AppLocalization.GetHintTextById('h355');
						this.hintText += " " + AppLocalization.GetHintTextById('h167');
					}
					else {
						this.suggestedCards = higherPlayerLeadSuitCards;

						// "h355": "You can beat the winning card.",
						// "h166": "Play your only lead suit card.",
						this.hintText = AppLocalization.GetHintTextById('h355');
						this.hintText += " " + AppLocalization.GetHintTextById('h166');
					}
					return;
				}
				// player does not have higher lead suit card than so far played
				// - player plays any lower lead suit card; suggest: lowest
				else {
					const playCard = this.GetLowestRankCard(playerLeadSuitCards, trumpSuit, trumpMode);
					this.playableCards = playerLeadSuitCards;
					this.suggestedCards = [playCard];
					this.botPlayCard = playCard;
					
					// console.log("player does not have higher lead suit card than so far played; player plays lowers lead suit card: "+playCard.log());

					if (this.playableCards.length > 1) {
						// "h352": "You can't beat the winning card.",
						// "h155": "Play a weak card.",
						this.hintText = AppLocalization.GetHintTextById('h352');
						this.hintText += " " + AppLocalization.GetHintTextById('h155');
					} else {
						// "h352": "You can't beat the winning card.",
						// "h350": "You have only one playable card.",
						this.hintText = AppLocalization.GetHintTextById('h352');
						this.hintText += " " + AppLocalization.GetHintTextById('h350');
					}
					return;
				}
			}
		}
		// if player can not follow lead suit:
		// - player can play ANY card
		else {
			// we are winning
			if (playerSide == trickWinnerSide) {
				const playCard = this.GetHighestRankCard(playerCards, trumpSuit, trumpMode);
				this.playableCards = playerCards;
				this.suggestedCards = [playCard];
				this.botPlayCard = playCard;

				// save points (TODO: keep unbeatable, if possible)
				if (this.playableCards.length > 1) {
					// "h301": "Your team is winning the trick."
					// "h357": "You don't have a lead suit card.",
					// "h450": "Save points.",
					this.hintText = AppLocalization.GetHintTextById('h301');
					this.hintText += " " + AppLocalization.GetHintTextById('h357');
					this.hintText += " " + AppLocalization.GetHintTextById('h450');
				}
				else {
					// "h350": "You have only one playable card.",
					this.hintText = AppLocalization.GetHintTextById('h350');
				}
				return;
			}
			// we are losing
			else {
				if (playerCards.length > 1) {
					const lowestCard = this.GetLowestRankCard(playerCards, trumpSuit, trumpMode);
					this.playableCards = playerCards;
					this.suggestedCards = [lowestCard];
					this.botPlayCard = lowestCard;
					
					// "h357": "You don't have a lead suit card.",
					// "h168": "Play any card.",
					this.hintText = AppLocalization.GetHintTextById('h357');
					this.hintText += " " + AppLocalization.GetHintTextById('h168');
					return;
				} 
				else {
					this.playableCards = playerCards;
					this.suggestedCards = playerCards;
					// this.botPlayCard = ;

					// "h350": "You have only one playable card.",
					this.hintText = AppLocalization.GetHintTextById('h350');
					return;
				}
			}
		}
	}

	/**
	 * Resolve winner player index and winning card index.. Store results in@see this.trickWinnerData
	 * NOTE: Can compute incomplete tricks as well, returning 'current winner of trick'
	 * - It is very important to understand, what this does. If only 1 player played, it returns data for that player.
	 *   This means that to effectively determine table state, this check should be used in conjunction with:
	 *   this.HasTeammatePlayed()
	 *
	 * Player indexes: 0|2 = @see PlayerSide.US
	 * Player indexes: 1|3 = @see PlayerSide.THEM
	 * -1 if no cards on table yet
	 *
	 */
	public GetTrickWinner(): TrickWinnerData {
		const trickCards: Card[] = this.table.tableStackPlay.GetCards();
		const firstStackCard: Card | null = this.table.tableStackPlay.GetFirst();
		const leadSuit: CardSuit = firstStackCard == null ? null : firstStackCard.suit;
		if (leadSuit == null) {
			this.trickWinnerData.playerIndex = -1;
			this.trickWinnerData.trickCardIndex = -1;
			return this.trickWinnerData;
		} else {
			const tableStateMachine: TableStateMachine = this.table.GetStateMachine();
			const tableData: TableData = tableStateMachine.GetData();
			const trumpSuit: TrumpSuit = tableData.trumpSuit;
			// const isLeadSuitTrumpSuit: boolean = this.IsCardSuitTrumpSuit(leadSuit, trumpSuit);
			// console.log("lastTrickDealerIndex:",dealerPlayerIndex, "trumpSuit:",TrumpSuit[trumpSuit],"leadSuit:",CardSuit[leadSuit]);

			let highestTrumpSuitIndex: number = -1;
			let highestLeadSuitIndex: number = -1;
			// let highestTrumpSuitValue: number = -1;
			// let highestLeadSuitValue: number = -1;
			let highestTrumpSuitRank: number = -1;
			let highestLeadSuitRank: number = -1;
			let cardProps: CardRankProps;
			switch (trumpSuit) {
				// ALL TRUMPS
				case TrumpSuit.ALLTRUMPS:
					const atPoints258: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
						OptionsItemKeys.atPoints258,
						this.table.variationId
					);
					if (atPoints258 && atPoints258.value) {
						trickCards.forEach((card, index) => {
							cardProps = CardsManager.cardRankToCardProps[card.rank];
							if (card.suit == leadSuit) {
								if (cardProps.rankOrderAllTrumps258 > highestLeadSuitRank) {
									highestLeadSuitRank = cardProps.rankOrderAllTrumps258;
									highestLeadSuitIndex = index;
								}
							}
						});
					} else {
						trickCards.forEach((card, index) => {
							cardProps = CardsManager.cardRankToCardProps[card.rank];
							if (card.suit == leadSuit) {
								if (cardProps.rankOrderAllTrumps > highestLeadSuitRank) {
									highestLeadSuitRank = cardProps.rankOrderAllTrumps;
									highestLeadSuitIndex = index;
								}
							}
						});
					}
					break;

				// NO TRUMPS
				case TrumpSuit.NOTRUMPS:
					const ntPoints130: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(
						OptionsItemKeys.ntPoints130,
						this.table.variationId
					);
					if (ntPoints130 && ntPoints130.value) {
						trickCards.forEach((card, index) => {
							cardProps = CardsManager.cardRankToCardProps[card.rank];
							if (card.suit == leadSuit) {
								if (cardProps.rankOrderNoTrumps130 > highestLeadSuitRank) {
									highestLeadSuitRank = cardProps.rankOrderNoTrumps130;
									highestLeadSuitIndex = index;
								}
							}
						});
					} else {
						trickCards.forEach((card, index) => {
							cardProps = CardsManager.cardRankToCardProps[card.rank];
							if (card.suit == leadSuit) {
								if (cardProps.rankOrderNoTrumps > highestLeadSuitRank) {
									highestLeadSuitRank = cardProps.rankOrderNoTrumps;
									highestLeadSuitIndex = index;
								}
							}
						});
					}
					break;

				// Trump and Other
				case TrumpSuit.CLUBS:
				case TrumpSuit.DIAMONDS:
				case TrumpSuit.HEARTS:
				case TrumpSuit.SPADES:
					trickCards.forEach((card, index) => {
						cardProps = CardsManager.cardRankToCardProps[card.rank];
						if (CardSuitToTrumpSuit(card.suit) == trumpSuit) {
							if (cardProps.rankOrderTrump > highestTrumpSuitRank) {
								highestTrumpSuitRank = cardProps.rankOrderTrump;
								highestTrumpSuitIndex = index;
							}
						} else if (card.suit == leadSuit) {
							if (cardProps.rankOrderOther > highestLeadSuitRank) {
								highestLeadSuitRank = cardProps.rankOrderOther;
								highestLeadSuitIndex = index;
							}
						}
					});
					// console.log("highestTrumpSuitIndex:",highestTrumpSuitIndex,"highestLeadSuitIndex:",highestLeadSuitIndex);
					break;
			}

			let cardWinIndex: number = -1;
			if (highestTrumpSuitIndex >= 0) {
				cardWinIndex = highestTrumpSuitIndex;
			} else if (highestLeadSuitIndex >= 0) {
				cardWinIndex = highestLeadSuitIndex;
			}

			// console.log("cardWinIndex:", cardWinIndex);
			if (cardWinIndex == -1) {
				this.trickWinnerData.playerIndex = -1;
				this.trickWinnerData.trickCardIndex = -1;
				return this.trickWinnerData;
			}

			const playDirection: GamePlayDirection = tableStateMachine.GetData().options.gamePlayDirection;
			this.trickWinnerData.playerIndex = tableStateMachine.GetFixedPlayerIndex(
				tableData.trickDealerIndex + playDirection * cardWinIndex
			);
			this.trickWinnerData.trickCardIndex = cardWinIndex;
			// console.log("trickWinnerIndex:",trickWinnerIndex);
			return this.trickWinnerData;
		}
	}

	public HasPlayerSuitCards(playerIndex: number, cardSuit: CardSuit): boolean {
		const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
		const playerCards: Card[] = player.GetCards();
		// NOTE: filter: return: A new array with the elements that pass the test. If no elements pass the test, an empty array will be returned.
		for (let i: number = 0; i < playerCards.length; i++) {
			const card: Card = playerCards[i];
			if (card.suit == cardSuit) {
				return true;
			}
		}
		return false;
	}

	protected IsCardInCollection(collection: Card[], suit: CardSuit, rank: CardRank): boolean {
		for (let i: number = 0; i < collection.length; i++) {
			const card: Card = collection[i];
			if (card.suit == suit && card.rank == rank) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Return card from player's hand, knowing he has it
	 * @param cardSuit 
	 * @param cardRank 
	 */
	public GetCardFromCollection(collection: Card[], cardSuit: CardSuit, cardRank: CardRank): Card | null {
		for (let i=collection.length-1; i>=0; i--) {
			const card: Card = collection[i];
			if (card.suit == cardSuit && card.rank == cardRank) {
				return card;
			}
		}
		return null;
	}

	/**
	 * Returns true if card was played already (in poolCards or in playStack cards)
	 */
	protected WasCardPlayed(cardSuit: CardSuit, cardRank: CardRank): boolean {
		const tableCards: Card[] = this.table.tableStackPlay.GetCards(); // no items = [] (empty array)
		const poolCards: Card[] = CardsManager.GetCards();
		if (this.IsCardInCollection(tableCards, cardSuit, cardRank) || 
		this.IsCardInCollection(poolCards, cardSuit, cardRank)) {
			return true;
		}
		return false;
	}

	public IsCardSuitTrumpSuit(cardSuit: CardSuit, trumpSuit: TrumpSuit): boolean {
		return CardSuitToTrumpSuit(cardSuit) == trumpSuit;
	}

	/**
	 * Returns highest card rank from collection. If suit is supplied, only returns highest for that suit
	 * @param cards
	 * @param filterSuit only consider cards of this suit
	 * @returns
	 */
	protected GetHighestRankCard(cards: Card[], trumpSuit: TrumpSuit, trumpMode: TrumpMode, filterSuit?: CardSuit | CardSuit[]): Card | null {
		let resultCard: Card = null;
		const single = typeof filterSuit == "number";
		cards.forEach((card) => {
			if (filterSuit == undefined || 
				((filterSuit != undefined) && (
					(single && (card.suit == filterSuit)) || 
					(!single && (filterSuit as CardSuit[]).indexOf(card.suit) != -1)
				))
			) {
				if (resultCard == null || (resultCard && card.GetRank(trumpMode, trumpSuit) > resultCard.GetRank(trumpMode, trumpSuit))) {
					resultCard = card;
				}
			}
		});
		return resultCard;
	}

	/**
	 *
	 * @param cards
	 * @param trumpSuit
	 * @param trumpMode
	 * @param filterSuit only consider cards of this suit
	 * @returns
	 */
	protected GetLowestRankCard(cards: Card[], trumpSuit: TrumpSuit, trumpMode: TrumpMode, filterSuit?: CardSuit | CardSuit[]): Card | null {
		// const filterSuit1 = CardSuit.CLUBS; // number
		// const filterSuit2 = [CardSuit.CLUBS]; // object
		// console.log(typeof filterSuit1, typeof filterSuit2);
		let resultCard: Card = null;
		const single = typeof filterSuit == "number";
		cards.forEach((card) => {
			if (filterSuit == undefined || 
				((filterSuit != undefined) && (
					(single && (card.suit == filterSuit)) || 
					(!single && (filterSuit as CardSuit[]).indexOf(card.suit) != -1)
				))
			) {
				if (resultCard == null || (resultCard && card.GetRank(trumpMode, trumpSuit) < resultCard.GetRank(trumpMode, trumpSuit))) {
					resultCard = card;
				}
			}
		});
		return resultCard;
	}

	/**
	 * Returns cards of rank higher than cardRank. if filterSuit is applied, returns only higher cards of same suit as filterSuit
	 * @param cards 
	 * @param trumpSuit 
	 * @param trumpMode 
	 * @param filterSuit 
	 * @returns 
	 */
	protected GetHigherRankCards(cards: Card[], trumpSuit: TrumpSuit, trumpMode: TrumpMode, cardRank: CardRank, filterSuit?: CardSuit): Card[] {
		let result: Card[] = cards.filter((card) => {
			if (filterSuit == undefined || (filterSuit != undefined && card.suit == filterSuit)) {
				return card.GetRank(trumpMode, trumpSuit) > cardRank;
			}
		});
		return result;
	}

	protected GetLowerRankCards(cards: Card[], trumpSuit: TrumpSuit, trumpMode: TrumpMode, cardRank: CardRank, filterSuit?: CardSuit): Card[] {
		let result: Card[] = cards.filter((card) => {
			if (filterSuit == undefined || (filterSuit != undefined && card.suit == filterSuit)) {
				return card.GetRank(trumpMode, trumpSuit) < cardRank;
			}
		});
		return result;
	}

	/**
     * (considering single suit only)
     * Returns all unbeatable cards, like this:
        // run rank from top to bottom: 
        // - if card in pool, continue.
        // - if on player, got unbeatable. add to results array.
        // - if neither, someone else's got it: unbeatable not on player; break; return results so far.
     * 
     * @param fromCards 
     * @param playedCards 
     * @param trumpMode Do treat as trump (rank calc) eg TrumpSuit.ALLTRUMPS, or NOT, eg TrumpSuit.NOTRUMPS
     */
	protected GetUnbeatableSuitCards(fromCards: Card[], playedCards: Card[], trumpMode: TrumpMode): Card[] {
		let unbeatableCards: Card[] = [];
		if (trumpMode == TrumpMode.TRUMP_OTHER) {
			console.log('Only TrumpMode.ALLTRUMPS and TrumpMode.NOTRUMPS is currently supported.');
			return unbeatableCards;
		}
		if (!fromCards.length) {
			return unbeatableCards;
		}
		// rank from top to bottom
		for (let i = 7; i >= 0; i--) {
			let foundCard: boolean = false;
			for (let j = 0; j < playedCards.length; j++) {
				if (playedCards[j].GetRank(trumpMode) == i) {
					foundCard = true;
					break;
				}
			}
			if (!foundCard) {
				for (let j = 0; j < fromCards.length; j++) {
					if (fromCards[j].GetRank(trumpMode) == i) {
						unbeatableCards.push(fromCards[j]);
						foundCard = true;
						break;
					}
				}
			}
			if (!foundCard) {
				return unbeatableCards;
			}
		}
		return unbeatableCards;
	}

	/**
	 * Returns bid value index
	 * @param bidValue Bid value as string as stored in loc variation file.
	 */
	protected BidValueToBidValueIndex(bidValue: string): number {
		return AppLocalization.GetVariationData().table.bidPopup.slider.choices.indexOf(bidValue);
	}

	protected IsDisabledCardSuit(cardSuit: CardSuit, disabledChoices: PopupBiddingChoiceType[]): boolean {
		const biddingChoiceType: PopupBiddingChoiceType = CardSuitToPopupBiddingChoiceType(cardSuit);
		return disabledChoices.indexOf(biddingChoiceType) != -1;
	}

	protected IsDisabledTrumpSuit(trumpSuit: TrumpSuit, disabledChoices: PopupBiddingChoiceType[]): boolean {
		const biddingChoiceType: PopupBiddingChoiceType = TrumpSuitToPopupBiddingChoiceType(trumpSuit);
		return disabledChoices.indexOf(biddingChoiceType) != -1;
	}

	/**
	 * Returns true, if card with this suit has been played in previous trick as a lead card, false otherwise
	 * @param cardSuit
	 */
	protected WasLeadTrickSuitPlayed(cardSuit: CardSuit): boolean {
		const historyTricksData: TrickData[] = this.table.GetStateMachine().GetData().historyTricksData;
		let wasPlayed: boolean = false;
		if (historyTricksData.length) {
			for (let i = 0; i < historyTricksData.length; i++) {
				const leadCardData: CardData = historyTricksData[i].cards[0];
				// console.log(CardSuit[leadCardData.suit], CardRank[leadCardData.rank]);
				if (cardSuit == leadCardData.suit) {
					return true;
				}
			}
		}
		return wasPlayed;
	}
}
